import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import logo from './Assests/Images/AgamizLogo.png';
import lostMaryBanner from './Assests/Images/LostMaryBanner.png';
import lostMaryWatermelon from './Assests/Images/BannerWatermelon.png';
import lostMaryPinkLemonade from './Assests/Images/BannerPinkLemonade.png';
import lostMaryScaryBerry from './Assests/Images/BannerScaryBerry.png';
import lostMaryStrawberry from './Assests/Images/BannerStrawberry.png';
import lostMaryTigersBlood from './Assests/Images/BannerTigersBlood.png';
import QweetLushIce from './Assests/Images/QweetLushIce.png';
import BannerAgamizGrinder from './Assests/Images/AgamizGrinder.png';
import bannerAgamizRollingPaper from './Assests/Images/AgamizRollingPaper.png';
import bannerRawRollingPaper from './Assests/Images/bannerRawRollingPaper.png';
import bannerPlasticBong from './Assests/Images/plasticBong.png';
import bannerWatermelonSnus from './Assests/Images/WatermelonCherry.png';
import bannerCherrySnus from './Assests/Images/CherrySnus.png';
import './App.css';

const CLIENT_ID = "ARHN6Ak_p0maRkCPCNRflKJbnGwnXja7r6D2aqCUvs4-h2w_g6erAFLamXeyIGNLTVfXeZjdpS9y7zlW";

const productImages = {
  "LostMaryWatermelon": lostMaryWatermelon,
  "lostMaryPinkLemonade": lostMaryPinkLemonade,
  "lostMaryScaryBerry": lostMaryScaryBerry,
  "lostMaryStrawberry": lostMaryStrawberry,
  "lostMaryTigersBlood": lostMaryTigersBlood,
  "QweetLushIce": QweetLushIce,
  "agamizGrinder": BannerAgamizGrinder,
  "agamizRollingPaper": bannerAgamizRollingPaper,
  "rawRollingPaper": bannerRawRollingPaper,
  "plasticBong": bannerPlasticBong,
  "cherrySnus": bannerCherrySnus,
  "watermelonSnus": bannerWatermelonSnus,
};

const PRIMARY  = 'https://api.old.agamiz.com';
const FALLBACK = 'https://187.77.173.87';

// Default to PRIMARY initially
export let API_BASE = PRIMARY;


export function initializeApiBase() {
  return new Promise((resolve) => {
    const ctrl  = new AbortController();
    const timer = setTimeout(() => {
      ctrl.abort();
      API_BASE = FALLBACK;
      console.warn(`[API_BASE] Primary API timed out. Switched to fallback: ${API_BASE}`);
      resolve(FALLBACK);
    }, 2500); // 2.5 second maximum wait time

    fetch(`${PRIMARY}/`, { method: 'HEAD', mode: 'no-cors', signal: ctrl.signal })
        .then(() => {
          clearTimeout(timer);
          console.log(`[API_BASE] Primary API is reachable. Using: ${API_BASE}`);
          resolve(PRIMARY);
        })
        .catch((err) => {
          clearTimeout(timer);
          API_BASE = FALLBACK;
          console.warn(`[API_BASE] Primary API unreachable. Switched to fallback: ${API_BASE}`);
          resolve(FALLBACK);
        });
  });
}

export async function apiFetch(path, options = {}) {
  try {
    const ctrl  = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 8000);

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      signal: options.signal ?? ctrl.signal,
    });

    clearTimeout(timer);
    return res;

  } catch (error) {
    // If the request fails and we weren't already using the fallback, switch now
    if (API_BASE !== FALLBACK) {
      console.warn(`[apiFetch] Request failed on ${API_BASE}. Switching dynamically to fallback.`);
      API_BASE = FALLBACK;
    }

    // Retry the request directly on the fallback IP
    return fetch(`${FALLBACK}${path}`, options);
  }
}

function getStoredAuthToken() {
  return localStorage.getItem('agamiz_auth_token') || '';
}

function getAdminSecret() {
  return sessionStorage.getItem('agamiz_admin_secret') || 'IvanMicheev1212';
}

function adminRequest(path, options = {}) {
  return apiRequest(path, { ...options, adminSecret: getAdminSecret() });
}

function isProductInStock(product) {
  if (!product) return false;
  if (typeof product.inStock === 'boolean') return product.inStock;
  if (typeof product.soldOut === 'boolean') return !product.soldOut;
  return true;
}

function useCaptchaConfig() {
  const [config, setConfig] = useState({
    enabled: true,
    siteKey: '0x4AAAAAADf3f3RXzHiYEepH',
  });

  useEffect(() => {
    apiRequest('/api/captcha/config')
        .then((data) => {
          setConfig({
            enabled: Boolean(data.enabled),
            siteKey: '0x4AAAAAADf3f3RXzHiYEepH',
          });
        })
        .catch(() => {});
  }, []);

  return config;
}

function CaptchaWidget({ siteKey, themeMode, onToken, resetKey }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    if (!siteKey) {
      onTokenRef.current('');
      return undefined;
    }

    let cancelled = false;

    const cleanupWidget = () => {
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {
          // Widget may already be removed.
        }
        widgetIdRef.current = null;
      }
    };

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      cleanupWidget();
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: themeMode === 'light' ? 'light' : 'dark',
        callback: (token) => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(''),
        'error-callback': () => onTokenRef.current(''),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.querySelector('script[data-turnstile]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.dataset.turnstile = 'true';
        document.head.appendChild(script);
      }
      script.addEventListener('load', renderWidget);
    }

    return () => {
      cancelled = true;
      cleanupWidget();
    };
  }, [siteKey, themeMode, resetKey]);

  if (!siteKey) return null;

  return (
      <div style={{ display: 'flex', justifyContent: 'center', minHeight: '65px' }}>
        <div ref={containerRef} />
      </div>
  );
}

async function apiRequest(path, { method = 'GET', body, token, ageToken, adminSecret } = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(ageToken ? { 'X-Age-Token': ageToken } : {}),
      ...(adminSecret ? { 'X-Admin-Secret': adminSecret } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }
  return data;
}

// Custom Hook to detect if the user is on a mobile device
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Check immediately on mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

// Mock dataset for the dynamic news section
const newsData = [
  {
    id: 1,
    titleEn: "New physical store coming soon!",
    titleHe: "בקרוב פתחית חנות פזיזית!",
    titleRu: "Скоро откроется новый физический магазин!",
    date: "2026-05-28",
    image: logo,
    descEn: "We're working on opening a physical store in Be'er Sheva.",
    descHe: "אנחנו עובדים על פתחית חנות בבאר שבע.",
    descRu: "Мы работаем над открытием физического магазина в Беэр-Шеве."
  }
];

/* ==========================================
   TRANSLATION DICTIONARY (EN / HE / RU)
   ========================================== */
const translations = {
  en: {
    dir: 'ltr',
    home: 'Home',
    shop: 'Shop',
    news: 'Gallery & News',
    aboutUs: 'About Us',
    cart: 'Cart',
    copyright: 'Agamiz Website. All rights reserved.',
    warning: 'WARNING: SOME PRODUCTS CONTAIN NICOTINE. NICOTINE IS AN ADDICTIVE CHEMICAL.',
    terms: 'Terms of Service',
    instantCheckout: 'Instant Checkout',
    directChannel: 'Direct transaction order channel',
    amountDue: 'Amount Due:',
    cartTitle: 'Your Shopping Cart',
    cartEmpty: 'Your shopping cart is empty.',
    subtotal: 'Subtotal:',
    ageRequired: 'Age Verification Required',
    ageNotice: 'You must be 18 years of age or older to enter this site. This verification expires after 30 days.',
    ageVerify: 'I am 18 or older - Enter Site',
    ageLeave: 'I am under 18 - Leave',
    welcome: '👋 Welcome to Agamiz 👋',
    heroTitle: 'Premium Electronic Cigarettes & Vaping Gear',
    heroSubtitle: 'Engineered for quality, priced for equity. Breaking the local monopoly pricing mechanics.',
    bannerNotice: '🔽 Lost Mary now available see for yourself 🔽',
    startShopping: 'Explore Catalog',
    vapes: 'Vapes',
    chooseVapes: 'Filter & Choose your hardware',
    buyNow: '⚡ Buy Now',
    addCart: '+ Cart',
    buyNowInstant: '⚡ Buy Now Instantly',
    addShoppingCart: 'Add to Shopping Cart',
    productMissing: 'Product Missing',
    configNotFound: 'The requested model configuration was not found in database.',
    returnShop: 'Return to Shop',
    backCatalog: '← Back to Catalog',
    verifiedHardware: 'Product',
    techSpecs: 'Specifications',
    missionTitle: 'Our Mission',
    deliveryFee: 'Delivery Fee (₪20 items)',
    total: 'Total',
    missionDesc: 'Time to fight the crazy vape prices here in Israel and start a new and fair market.',
    lastUpdated: 'Last Updated: May 2026',
    paypalArch: 'All transactions initialized on Agamiz website are safely executed utilizing the secure external handling architecture of PayPal systems.',
    pageNotFound: 'Page Not Found',
    soldOut: 'Sold Out via POS',
    filterAll: 'All Products',
    filterHighCap: 'High Capacity',
    filterTurbo: 'Disposable',
    filterGrinder: 'Grinders',
    filterRollingPaper: 'Rolling Paper',
    filterBong: 'Bongs',
    filterSnus: 'Snus',
    localDelivery: 'Local Be\'er Sheva Delivery (Free Same-Day)',
    calculatorTitle: 'Lifespan Estimator',
    calcInput: 'Average daily puffs:',
    calcResult: 'Estimated Lifespan:',
    days: 'Days',
    searchPlaceholder: 'Search products by name...',
    latestNews: 'Latest Updates & Gallery Feed',
    descriptionNews: 'Stay updated with our latest news about us and products in the store itself.',
    publishedOn: 'Published on:',
    exploreGallery: 'Product Spotlights',
    notifyMe: '🔔 Notify Me',
    notifyDesc: 'Get alerted when this item is back in stock',
    notifyInput: 'Email or phone number',
    notifySubscribe: 'Subscribe',
    notifySuccess: "✅ You'll be notified when it's back!",
    notifyAlready: '🔔 Subscribed',
    notifyError: 'Could not subscribe. Please try again.',
    notifySubmitting: 'Subscribing...',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    signOut: 'Sign Out',
    myProfile: 'Profile',
    orderHistory: 'Order History',
    noOrders: 'No orders yet.',
    reorder: '↺ Reorder',
    accountName: 'Full Name',
    accountEmail: 'Email Address',
    accountPassword: 'Password',
    createAccount: 'Create Account',
    haveAccount: 'Already have an account? Sign In',
    noAccount: "Don't have an account? Sign Up",
    loginError: 'Invalid email or password.',
    registerError: 'An account with this email already exists.',
    emailNotVerified: 'Please verify your email before signing in.',
    verificationEmailSent: 'Account created. Check your email to verify before signing in.',
    ageServerError: 'Age verification could not be saved. Please try again.',
    profileTitle: 'My Account',
    savedPaymentNote: 'Payments securely processed via PayPal.',
    orderId: 'Order',
    orderTotal: 'Total',
    orderDate: 'Date',
    orderItems: 'items',
    paypalLinked: 'PayPal Account Linked',
    deleteAccount: 'Delete Account',
    deleteAccountTitle: 'Delete Your Account?',
    deleteAccountDesc: 'This permanently removes your account, order history, and stock alert subscriptions. This cannot be undone.',
    deleteAccountPassword: 'Enter your password to confirm',
    deleteAccountConfirm: 'Permanently Delete',
    deleteAccountCancel: 'Cancel',
    deleteAccountError: 'Incorrect password. Account was not deleted.',
    deleteAccountSuccess: 'Your account has been deleted.',
    privacy: 'Privacy Policy',
    faq: 'FAQ',
    stockAlerts: 'Stock Alerts',
    noStockAlerts: 'No active stock alerts.',
    unsubscribe: 'Unsubscribe',
    nicotineDisclaimer: 'This product contains nicotine. Nicotine is an addictive chemical. For adult use only (18+).',
    trustAge: '18+ Only',
    trustNicotine: 'Nicotine Warning',
    trustPaypal: 'PayPal Secure',
    trustDelivery: 'Same-Day Delivery',
    cartQty: 'Qty',
    privacyTitle: 'Privacy Policy',
    faqTitle: 'Frequently Asked Questions',
    faqAge: 'Who can purchase from Agamiz?',
    faqAgeA: 'You must be 18 years or older. We verify age on entry and require email verification for accounts.',
    faqShipping: 'How does delivery work?',
    faqShippingA: 'We offer free same-day local delivery in Be\'er Sheva and surrounding areas. Select the delivery option at checkout.',
    faqReturns: 'What is your return policy?',
    faqReturnsA: 'Unopened products in original packaging may be returned within 7 days. Nicotine products cannot be returned once opened due to health regulations.',
    faqPayment: 'What payment methods do you accept?',
    faqPaymentA: 'All payments are processed securely through PayPal. We do not store card details on our servers.',
    faqNicotine: 'Are your products regulated?',
    faqNicotineA: 'We sell verified hardware from reputable manufacturers. Products may contain nicotine — an addictive chemical. Use responsibly and keep away from children.',
    privacyData: 'Data We Collect',
    privacyDataDesc: 'We collect your name, email, order history, and PayPal email (when provided at checkout) to process orders and manage your account. Age verification sessions are logged with hashed identifiers.',
    privacyUse: 'How We Use Your Data',
    privacyUseDesc: 'Your data is used solely to fulfill orders, send stock alerts you subscribed to, verify your account, and improve our service. We never sell your personal information.',
    privacyDelete: 'Your Right to Delete',
    privacyDeleteDesc: 'You can permanently delete your account and all associated data from your profile at any time.',
    privacyContact: 'Contact',
    privacyContactDesc: 'For privacy inquiries, email us at support@old.agamiz.com',
    acceptTermsPrefix: 'I agree to the',
    acceptTermsAnd: 'and',
    acceptTermsError: 'You must accept the Terms of Service and Privacy Policy',
    marketingOptIn: 'Send me updates, deals, and new product announcements',
    forgotPassword: 'Forgot password?',
    forgotPasswordTitle: 'Reset Password',
    forgotPasswordDesc: 'Enter your email and we will send you a reset link.',
    sendResetLink: 'Send Reset Link',
    resetLinkSent: 'If an account exists, a reset link has been sent.',
    backToSignIn: 'Back to Sign In',
    resendVerification: 'Resend verification email',
    resendVerificationSent: 'Verification email sent. Check your inbox.',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    resetPasswordTitle: 'Set New Password',
    resetPassword: 'Update Password',
    resetPasswordSuccess: 'Password updated. You can now sign in.',
    resetPasswordFailed: 'Reset failed. The link may have expired.',
    passwordsMismatch: 'Passwords do not match',
    adminTitle: 'Admin Panel',
    adminLogin: 'Admin Login',
    adminSecretLabel: 'Admin Secret Key',
    adminEnter: 'Enter Panel',
    adminInvalid: 'Invalid admin key',
    adminStock: 'Stock Management',
    adminBroadcast: 'Send Updates',
    adminBroadcastSubject: 'Email Subject',
    adminBroadcastTitle: 'Headline',
    adminBroadcastMessage: 'Message',
    adminSendBroadcast: 'Send to Subscribers',
    adminBroadcastSuccess: 'Broadcast sent successfully',
    adminBroadcastEmpty: 'Fill in subject, headline, and message',
    adminSubscribers: 'Marketing subscribers',
    adminProducts: 'Products',
    adminOrders: 'Orders',
    adminUsers: 'Users',
    adminInStock: 'In Stock',
    adminSoldOut: 'Sold Out',
    adminMarkSoldOut: 'Mark Sold Out',
    adminMarkInStock: 'Mark In Stock',
    adminLogout: 'Exit Admin',
    adminNotified: 'stock alerts sent',
    marketingUnsubscribed: 'You have been unsubscribed from Agamiz updates.',
    marketingUnsubFailed: 'Could not unsubscribe. The link may have expired.',
    captchaRequired: 'Please complete the CAPTCHA verification.',
  },
  he: {
    dir: 'rtl',
    home: 'בית',
    shop: 'חנות',
    news: 'גלריה וחדשות',
    aboutUs: 'עלינו',
    cart: 'עגלה',
    copyright: 'אתר אגמיז. כל הזכויות שמורות.',
    warning: 'אזהרה: מוצרים מסוימים מכילים ניקוטין. ניקוטין הוא חומר כימי ממכר.',
    terms: 'תנאי שימוש',
    instantCheckout: 'רכישה מהירה',
    directChannel: 'ערוץ הזמנה ישיר ומאובטח',
    amountDue: 'סכום לתשלום:',
    cartTitle: 'עגלת הקניות שלך',
    cartEmpty: 'עגלת הקניות שלך ריקה.',
    subtotal: 'סך הכל:',
    ageRequired: 'נדרש אימות גיל',
    ageNotice: 'עליך להיות בן 18 ומעלה כדי להיכנס לאתר זה. אימות זה פג לאחר 30 יום.',
    ageVerify: 'אני בן 18 ומעלה - כניסה לאתר',
    deliveryFee: 'דמי משלוח (פריטי ₪20)',
    total: 'סה"כ',
    ageLeave: 'אני מתחת לגיל 18 - יציאה',
    welcome: '👋 ברוכים הבאים לאגמיז 👋',
    heroTitle: 'סיגריות אלקטרוניות וכלי עישון פרימיום',
    heroSubtitle: 'הנדסת מוצר ברמה הגבוהה ביותר, במחיר הוגן. שוברים את מונופול המחירים בישראל.',
    bannerNotice: '🔽 Lost Mary זמין עכשיו, תראו בעצמכם🔽',
    startShopping: 'התחל לקנות',
    vapes: 'וייפים',
    chooseVapes: 'סנן ובחר את המכשיר שלך',
    buyNow: '⚡ קנה עכשיו',
    addCart: '+ לעגלה',
    buyNowInstant: '⚡ רכישה מיידית',
    addShoppingCart: 'הוסף לעגלת הקניות',
    productMissing: 'המוצר חסר',
    configNotFound: 'תצורת המודל המבוקשת לא נמצאה במאגר הנתונים.',
    returnShop: 'חזור לחנות',
    backCatalog: '← חזרה לקטלוג',
    verifiedHardware: 'מהדורת חומרה מאומתת',
    techSpecs: 'מפרט',
    missionTitle: 'החזון שלנו',
    missionDesc: 'הגיע הזמן להילחם במחירי הוייפים המטורפים כאן בישראל ולהתחיל שוק חדש והוגן.',
    lastUpdated: 'עודכן לאחרונה: מאי 2026',
    paypalArch: 'כל העסקאות המתבצעות באתר אגמיז מבוצעות בצורה מאובטחת באמצעות ארכיטקטורת הסליקה החיצונית של מערכות PayPal.',
    pageNotFound: 'העמוד לא נמצא',
    soldOut: 'אזל במלאי הקופה',
    filterAll: 'כל המוצרים',
    filterHighCap: 'קיבולת גבוהה',
    filterTurbo: 'חד פעמי',
    filterGrinder: 'גריינדרים',
    filterBong: 'באנגים',
    filterSnus: 'סנוס',
    filterRollingPaper: 'נייר גלגול',
    localDelivery: 'משלוח מקומי בבאר שבע (חינם מהיום להיום)',
    calculatorTitle: 'מחשבון אורך חיים',
    calcInput: 'שאיפות ממוצעות ביום:',
    calcResult: 'אורך חיים משוער:',
    days: 'ימים',
    searchPlaceholder: 'חפש מוצרים לפי שם...',
    latestNews: 'עדכונים חמים ופיד גלריה',
    descriptionNews: 'הישארו מעודכנים עלינו ועל מוצרים בחנות עצמה.',
    publishedOn: 'פורסם בתאריך:',
    exploreGallery: 'הצצה למוצרים המובילים',
    notifyMe: '🔔 הודע לי',
    notifyDesc: 'קבל התראה כשהמוצר יחזור למלאי',
    notifyInput: 'אימייל או מספר טלפון',
    notifySubscribe: 'הרשמה',
    notifySuccess: '✅ תקבל התראה כשיחזור למלאי!',
    notifyAlready: '🔔 נרשמת',
    notifyError: 'לא ניתן להירשם. נסה שוב.',
    notifySubmitting: 'נרשם...',
    signIn: 'התחבר',
    signUp: 'הרשמה',
    signOut: 'התנתק',
    myProfile: 'פרופיל',
    orderHistory: 'היסטוריית הזמנות',
    noOrders: 'עדיין אין הזמנות.',
    reorder: '↺ הזמן שוב',
    accountName: 'שם מלא',
    accountEmail: 'כתובת אימייל',
    accountPassword: 'סיסמה',
    createAccount: 'צור חשבון',
    haveAccount: 'יש לך כבר חשבון? התחבר',
    noAccount: 'אין לך חשבון? הרשם',
    loginError: 'אימייל או סיסמה שגויים.',
    registerError: 'חשבון עם אימייל זה כבר קיים.',
    emailNotVerified: 'יש לאמת את האימייל לפני התחברות.',
    verificationEmailSent: 'החשבון נוצר. בדקו את האימייל כדי לאמת אותו לפני התחברות.',
    ageServerError: 'לא ניתן לשמור את אימות הגיל. נסו שוב.',
    profileTitle: 'החשבון שלי',
    savedPaymentNote: 'תשלומים מעובדים בבטחה דרך PayPal.',
    orderId: 'הזמנה',
    orderTotal: 'סה"כ',
    orderDate: 'תאריך',
    orderItems: 'פריטים',
    paypalLinked: 'PayPal מקושר',
    deleteAccount: 'מחק חשבון',
    deleteAccountTitle: 'למחוק את החשבון?',
    deleteAccountDesc: 'פעולה זו מוחקת לצמיתות את החשבון, היסטוריית ההזמנות והתראות המלאי. לא ניתן לבטל.',
    deleteAccountPassword: 'הזן סיסמה לאישור',
    deleteAccountConfirm: 'מחק לצמיתות',
    deleteAccountCancel: 'ביטול',
    deleteAccountError: 'סיסמה שגויה. החשבון לא נמחק.',
    deleteAccountSuccess: 'החשבון שלך נמחק.',
    privacy: 'מדיניות פרטיות',
    faq: 'שאלות נפוצות',
    stockAlerts: 'התראות מלאי',
    noStockAlerts: 'אין התראות מלאי פעילות.',
    unsubscribe: 'בטל הרשמה',
    nicotineDisclaimer: 'מוצר זה מכיל ניקוטין. ניקוטין הוא חומר ממכר. לשימוש מבוגרים בלבד (18+).',
    trustAge: '18+ בלבד',
    trustNicotine: 'אזהרת ניקוטין',
    trustPaypal: 'PayPal מאובטח',
    trustDelivery: 'משלוח מהיום להיום',
    cartQty: 'כמות',
    privacyTitle: 'מדיניות פרטיות',
    faqTitle: 'שאלות נפוצות',
    faqAge: 'מי יכול לרכוש באגמיז?',
    faqAgeA: 'עליך להיות בן 18 ומעלה. אנו מאמתים גיל בכניסה ודורשים אימות אימייל לחשבונות.',
    faqShipping: 'איך עובד המשלוח?',
    faqShippingA: 'משלוח מקומי חינם מהיום להיום בבאר שבע והסביבה. בחרו באפשרות המשלוח בקופה.',
    faqReturns: 'מה מדיניות ההחזרות?',
    faqReturnsA: 'מוצרים שלא נפתחו באריזה מקורית ניתנים להחזרה תוך 7 ימים. מוצרי ניקוטין לא ניתנים להחזרה לאחר פתיחה.',
    faqPayment: 'אילו אמצעי תשלום מקובלים?',
    faqPaymentA: 'כל התשלומים מעובדים בבטחה דרך PayPal. איננו שומרים פרטי כרטיס אשראי.',
    faqNicotine: 'האם המוצרים מוסדרים?',
    faqNicotineA: 'אנו מוכרים חומרה מאומתת מיצרנים מוכרים. מוצרים עשויים להכיל ניקוטין — חומר ממכר.',
    privacyData: 'נתונים שאנו אוספים',
    privacyDataDesc: 'אנו אוספים שם, אימייל, היסטוריית הזמנות ואימייל PayPal לעיבוד הזמנות וניהול חשבון.',
    privacyUse: 'כיצד אנו משתמשים בנתונים',
    privacyUseDesc: 'הנתונים משמשים למילוי הזמנות, שליחת התראות מלאי, אימות חשבון ושיפור השירות. לא מוכרים מידע אישי.',
    privacyDelete: 'זכות המחיקה שלך',
    privacyDeleteDesc: 'ניתן למחוק לצמיתות את החשבון וכל הנתונים מהפרופיל בכל עת.',
    privacyContact: 'יצירת קשר',
    privacyContactDesc: 'לפניות פרטיות: support@old.agamiz.com',
    acceptTermsPrefix: 'אני מסכים/ה ל',
    acceptTermsAnd: 'ו',
    acceptTermsError: 'יש לאשר את תנאי השימוש ומדיניות הפרטיות',
    marketingOptIn: 'שלחו לי עדכונים, מבצעים והודעות על מוצרים חדשים',
    forgotPassword: 'שכחת סיסמה?',
    forgotPasswordTitle: 'איפוס סיסמה',
    forgotPasswordDesc: 'הזן את האימייל שלך ונשלח קישור לאיפוס.',
    sendResetLink: 'שלח קישור איפוס',
    resetLinkSent: 'אם קיים חשבון, נשלח קישור איפוס.',
    backToSignIn: 'חזרה להתחברות',
    resendVerification: 'שלח שוב אימייל אימות',
    resendVerificationSent: 'אימייל אימות נשלח. בדוק את תיבת הדואר.',
    newPassword: 'סיסמה חדשה',
    confirmPassword: 'אימות סיסמה',
    resetPasswordTitle: 'הגדר סיסמה חדשה',
    resetPassword: 'עדכן סיסמה',
    resetPasswordSuccess: 'הסיסמה עודכנה. אפשר להתחבר.',
    resetPasswordFailed: 'האיפוס נכשל. ייתכן שהקישור פג תוקף.',
    passwordsMismatch: 'הסיסמאות אינן תואמות',
    adminTitle: 'פאנל ניהול',
    adminLogin: 'כניסת מנהל',
    adminSecretLabel: 'מפתח מנהל',
    adminEnter: 'כניסה',
    adminInvalid: 'מפתח מנהל שגוי',
    adminStock: 'ניהול מלאי',
    adminBroadcast: 'שליחת עדכונים',
    adminBroadcastSubject: 'נושא האימייל',
    adminBroadcastTitle: 'כותרת',
    adminBroadcastMessage: 'הודעה',
    adminSendBroadcast: 'שלח למנויים',
    adminBroadcastSuccess: 'העדכון נשלח בהצלחה',
    adminBroadcastEmpty: 'מלא נושא, כותרת והודעה',
    adminSubscribers: 'מנויי עדכונים',
    adminProducts: 'מוצרים',
    adminOrders: 'הזמנות',
    adminUsers: 'משתמשים',
    adminInStock: 'במלאי',
    adminSoldOut: 'אזל',
    adminMarkSoldOut: 'סמן כאזל',
    adminMarkInStock: 'סמן במלאי',
    adminLogout: 'יציאה',
    adminNotified: 'התראות מלאי נשלחו',
    marketingUnsubscribed: 'בוטלת ההרשמה לעדכונים מאגמיז.',
    marketingUnsubFailed: 'לא ניתן לבטל הרשמה. ייתכן שהקישור פג תוקף.',
    captchaRequired: 'יש להשלים את אימות ה-CAPTCHA.',
  },
  ru: {
    dir: 'ltr',
    home: 'Главная',
    shop: 'Магазин',
    news: 'Новости и Галерея',
    aboutUs: 'О нас',
    cart: 'Корзина',
    copyright: 'Сайт Agamiz. Все права защищены.',
    warning: 'ПРЕДУПРЕЖДЕНИЕ: НЕКОТОРЫЕ ПРОДУКТЫ СОДЕРЖАТ НИКОТИН. НИКОТИН ВЫЗЫВАЕТ ЗАВИСИМОСТЬ.',
    terms: 'Условия использования',
    instantCheckout: 'Быстрая покупка',
    directChannel: 'Прямой канал оформления заказа',
    amountDue: 'Итого к оплате:',
    cartTitle: 'Ваша корзина',
    cartEmpty: 'Ваша корзина пуста.',
    subtotal: 'Подытог:',
    ageRequired: 'Требуется подтверждение возраста',
    ageNotice: 'Вам должно быть 18 или более лет для входа на сайт. Подтверждение истекает через 30 дней.',
    ageVerify: 'Мне есть 18 лет - Войти',
    ageLeave: 'Мне нет 18 лет - Покинуть сайт',
    welcome: '👋 Добро пожаловать в Agamiz 👋',
    heroTitle: 'Электронные сигареты и вейпы премиум-класса',
    heroSubtitle: 'Создано для качества, оценено по справедливости. Ломаем местную ценовую монополию.',
    bannerNotice: '🔽 Lost Mary уже в наличии, убедитесь сами🔽',
    startShopping: 'Начать покупки',
    deliveryFee: 'Стоимость доставки (товары ₪20)',
    total: 'Итого',
    vapes: 'Вейпы',
    chooseVapes: 'Фильтр и выбор устройств',
    buyNow: '⚡ Купить',
    addCart: '+ Корзина',
    buyNowInstant: '⚡ Купить мгновенно',
    addShoppingCart: 'Добавить в корзину',
    productMissing: 'Товар отсутствует',
    configNotFound: 'Запрошенная конфигурация модели не найдена в базе данных.',
    returnShop: 'Вернуться в магазин',
    backCatalog: '← Назад в каталог',
    verifiedHardware: 'Проверенное аппаратное издание',
    techSpecs: 'Характеристики',
    missionTitle: 'Наша миссия',
    missionDesc: 'Пора бороться с сумасшедшими ценами на вейпы здесь, в Израиле, и начать новый, честный рынок.',
    lastUpdated: 'Последнее обновление: Май 2026',
    paypalArch: 'Все транзакции безопасно выполняются системами PayPal.',
    pageNotFound: 'Page Not Found',
    soldOut: 'Распродано (POS)',
    filterAll: 'Все модели',
    filterHighCap: 'Высокая емкость',
    filterTurbo: 'Одноразовый',
    filterGrinder: 'Измельчители',
    filterBong: 'Бонги',
    filterSnus: 'Снус',
    filterRollingPaper: 'Бумага для самокруток',
    localDelivery: 'Местная доставка в Беэр-Шеве (Бесплатно сегодня)',
    calculatorTitle: 'Калькулятор срока службы',
    calcInput: 'Среднее количество затяжек в день:',
    calcResult: 'Примерный срок службы:',
    days: 'Дней',
    searchPlaceholder: 'Поиск товаров по названию...',
    latestNews: 'Последние Обновления и Лента',
    descriptionNews: 'Будьте в курсе последних новостей о нас и товарах в нашем магазине.',
    publishedOn: 'Опубликовано:',
    exploreGallery: 'Обзор Устройств',
    notifyMe: '🔔 Уведомить',
    notifyDesc: 'Получить уведомление, когда товар снова появится в наличии',
    notifyInput: 'Email или номер телефона',
    notifySubscribe: 'Подписаться',
    notifySuccess: '✅ Вы получите уведомление о наличии!',
    notifyAlready: '🔔 Вы подписаны',
    notifyError: 'Не удалось подписаться. Попробуйте снова.',
    notifySubmitting: 'Подписка...',
    signIn: 'Войти',
    signUp: 'Регистрация',
    signOut: 'Выйти',
    myProfile: 'Профиль',
    orderHistory: 'История заказов',
    noOrders: 'Нет заказов.',
    reorder: '↺ Повторить заказ',
    accountName: 'Полное имя',
    accountEmail: 'Email адрес',
    accountPassword: 'Пароль',
    createAccount: 'Создать аккаунт',
    haveAccount: 'Уже есть аккаунт? Войти',
    noAccount: 'Нет аккаунта? Зарегистрироваться',
    loginError: 'Неверный email или пароль.',
    registerError: 'Аккаунт с этим email уже существует.',
    emailNotVerified: 'Подтвердите email перед входом.',
    verificationEmailSent: 'Аккаунт создан. Проверьте email и подтвердите его перед входом.',
    ageServerError: 'Не удалось сохранить подтверждение возраста. Попробуйте снова.',
    profileTitle: 'Мой аккаунт',
    savedPaymentNote: 'Платежи безопасно обрабатываются через PayPal.',
    orderId: 'Заказ',
    orderTotal: 'Итого',
    orderDate: 'Дата',
    orderItems: 'товаров',
    paypalLinked: 'PayPal подключён',
    deleteAccount: 'Удалить аккаунт',
    deleteAccountTitle: 'Удалить аккаунт?',
    deleteAccountDesc: 'Это навсегда удалит ваш аккаунт, историю заказов и подписки на уведомления. Отменить нельзя.',
    deleteAccountPassword: 'Введите пароль для подтверждения',
    deleteAccountConfirm: 'Удалить навсегда',
    deleteAccountCancel: 'Отмена',
    deleteAccountError: 'Неверный пароль. Аккаунт не удалён.',
    deleteAccountSuccess: 'Ваш аккаунт удалён.',
    privacy: 'Политика конфиденциальности',
    faq: 'FAQ',
    stockAlerts: 'Уведомления о наличии',
    noStockAlerts: 'Нет активных подписок.',
    unsubscribe: 'Отписаться',
    nicotineDisclaimer: 'Продукт содержит никотин. Никотин вызывает зависимость. Только для взрослых (18+).',
    trustAge: 'Только 18+',
    trustNicotine: 'Предупреждение о никотине',
    trustPaypal: 'Безопасный PayPal',
    trustDelivery: 'Доставка в день заказа',
    cartQty: 'Кол-во',
    privacyTitle: 'Политика конфиденциальности',
    faqTitle: 'Часто задаваемые вопросы',
    faqAge: 'Кто может покупать в Agamiz?',
    faqAgeA: 'Вам должно быть 18 или более лет. Мы проверяем возраст при входе и требуем подтверждение email.',
    faqShipping: 'Как работает доставка?',
    faqShippingA: 'Бесплатная доставка в день заказа в Беэр-Шеве и окрестностях. Выберите доставку при оформлении.',
    faqReturns: 'Какова политика возврата?',
    faqReturnsA: 'Невскрытые товары в оригинальной упаковке можно вернуть в течение 7 дней. Никотиновые продукты не подлежат возврату после вскрытия.',
    faqPayment: 'Какие способы оплаты принимаются?',
    faqPaymentA: 'Все платежи обрабатываются через PayPal. Мы не храним данные карт.',
    faqNicotine: 'Регулируются ли ваши продукты?',
    faqNicotineA: 'Мы продаём проверенные устройства от надёжных производителей. Продукты могут содержать никотин.',
    privacyData: 'Какие данные мы собираем',
    privacyDataDesc: 'Имя, email, история заказов и PayPal email для обработки заказов и управления аккаунтом.',
    privacyUse: 'Как мы используем данные',
    privacyUseDesc: 'Данные используются для выполнения заказов, уведомлений о наличии и верификации. Мы не продаём личную информацию.',
    privacyDelete: 'Право на удаление',
    privacyDeleteDesc: 'Вы можете навсегда удалить аккаунт и все данные из профиля в любое время.',
    privacyContact: 'Контакты',
    privacyContactDesc: 'По вопросам конфиденциальности: support@old.agamiz.com',
    acceptTermsPrefix: 'Я согласен с',
    acceptTermsAnd: 'и',
    acceptTermsError: 'Необходимо принять Условия и Политику конфиденциальности',
    marketingOptIn: 'Присылать обновления, акции и новости о продуктах',
    forgotPassword: 'Забыли пароль?',
    forgotPasswordTitle: 'Сброс пароля',
    forgotPasswordDesc: 'Введите email — мы отправим ссылку для сброса.',
    sendResetLink: 'Отправить ссылку',
    resetLinkSent: 'Если аккаунт существует, ссылка отправлена.',
    backToSignIn: 'Назад ко входу',
    resendVerification: 'Отправить письмо повторно',
    resendVerificationSent: 'Письмо отправлено. Проверьте почту.',
    newPassword: 'Новый пароль',
    confirmPassword: 'Подтвердите пароль',
    resetPasswordTitle: 'Новый пароль',
    resetPassword: 'Обновить пароль',
    resetPasswordSuccess: 'Пароль обновлён. Можно войти.',
    resetPasswordFailed: 'Сброс не удался. Ссылка могла истечь.',
    passwordsMismatch: 'Пароли не совпадают',
    adminTitle: 'Панель администратора',
    adminLogin: 'Вход администратора',
    adminSecretLabel: 'Секретный ключ',
    adminEnter: 'Войти',
    adminInvalid: 'Неверный ключ',
    adminStock: 'Управление складом',
    adminBroadcast: 'Рассылка обновлений',
    adminBroadcastSubject: 'Тема письма',
    adminBroadcastTitle: 'Заголовок',
    adminBroadcastMessage: 'Сообщение',
    adminSendBroadcast: 'Отправить подписчикам',
    adminBroadcastSuccess: 'Рассылка отправлена',
    adminBroadcastEmpty: 'Заполните тему, заголовок и сообщение',
    adminSubscribers: 'Подписчики на рассылку',
    adminProducts: 'Товары',
    adminOrders: 'Заказы',
    adminUsers: 'Пользователи',
    adminInStock: 'В наличии',
    adminSoldOut: 'Нет в наличии',
    adminMarkSoldOut: 'Отметить распроданным',
    adminMarkInStock: 'Отметить в наличии',
    adminLogout: 'Выйти',
    adminNotified: 'уведомлений о наличии отправлено',
    marketingUnsubscribed: 'Вы отписаны от рассылки Agamiz.',
    marketingUnsubFailed: 'Не удалось отписаться. Ссылка могла истечь.',
    captchaRequired: 'Пожалуйста, пройдите проверку CAPTCHA.',
  }
};

const themes = {
  light: { id: 'light', name: '☀️ Light', bg: "#f8fafc", cardBg: "#ffffff", factorBg: "#f1f5f9", text: "#0f172a", textMuted: "#64748b", border: "#e2e8f0", badgeBg: "#e0f2fe", badgeText: "#0369a1", accent: "#0070ba", industrialAccent: "#38bdf8" },
  dark: { id: 'dark', name: '🌙 Dark', bg: "#0f172a", cardBg: "#1e293b", factorBg: "#111827", text: "#f8fafc", textMuted: "#94a3b8", border: "#334155", badgeBg: "#1e293b", badgeText: "#38bdf8", accent: "#0070ba", industrialAccent: "#38bdf8" },
  rugged: { id: 'rugged', name: '⚙️ Rugged', bg: "#111111", cardBg: "#1a1a1a", factorBg: "#222222", text: "#f97316", textMuted: "#a3a3a3", border: "#404040", badgeBg: "#451a03", badgeText: "#fdba74", accent: "#ea580c", industrialAccent: "#f97316" }
};

const hardwareIcons = {
  "Capacity": "💧", "Battery": "🔋", "Puffs": "💨", "Coil": "🔥", "Charging": "⚡"
};

function useProducts() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadProducts = useCallback(async () => {
    try {
      const data = await apiRequest('/api/products');
      setProducts(data.products || []);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    const interval = setInterval(loadProducts, 3000);
    return () => clearInterval(interval);
  }, [loadProducts]);

  return { products, isLoading, refreshProducts: loadProducts };
}

function App() {
  const [isApiReady, setIsApiReady] = useState(false);
  const isMobile = useIsMobile(); // Initialize phone detection
  const captchaConfig = useCaptchaConfig();
  const DELIVERY_FEE = 20;
  const DELIVERY_FEE_PRICE_THRESHOLD = 20;

// Add this helper function near the other helper functions
  function isSnusCategory(product) {
    if (!product) return false;

    // Check category field
    if (product.category && String(product.category).toLowerCase() === 'snus') {
      return true;
    }

    // Check description for snus
    const desc = (product.description || '').toLowerCase();
    if (desc.includes('snus')) return true;

    // Check specs modes for snus
    const modes = product.specs?.Modes || product.specs?.modes || [];
    if (Array.isArray(modes)) {
      if (modes.some(m => String(m).toLowerCase().includes('snus'))) return true;
    } else if (String(modes).toLowerCase().includes('snus')) {
      return true;
    }

    return false;
  }

  function requiresDeliveryFee(item) {
    if (!item) return false;

    // Exclude snus category from delivery fees
    if (isSnusCategory(item)) return false;

    // Check if price is under 20 shekels
    const price = parseFloat(item.price);
    return price < DELIVERY_FEE_PRICE_THRESHOLD;
  }

  useEffect(() => {
    initializeApiBase().then(() => setIsApiReady(true));
    const timer = setTimeout(() => {
      setIsApiReady(true);
    }, 1300); // 5000 milliseconds = 5 seconds
    return () => clearTimeout(timer);
  }, []);

  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('agamiz_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [ageToken, setAgeToken] = useState(() => localStorage.getItem('agamiz_age_token') || '');
  const [isAgeVerified, setIsAgeVerified] = useState(() => !!localStorage.getItem('agamiz_age_token'));

  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('agamiz_lang');
    if (saved) return saved;
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLang.startsWith('he') ? 'he' : 'en';
  });

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [themeMode, setThemeMode] = useState('dark');
  const [instantBuyProduct, setInstantBuyProduct] = useState(null);
  const [localDeliveryEnabled, setLocalDeliveryEnabled] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  // ── NEW: Auth & Profile State ──────────────────────────────────────────
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('agamiz_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('agamiz_auth_token') || '');
  useEffect(() => {
    if (currentUser) localStorage.setItem('agamiz_user', JSON.stringify(currentUser));
    else localStorage.removeItem('agamiz_user');
  }, [currentUser]);
  const [orders, setOrders] = useState(() => {
    const saved = localStorage.getItem('agamiz_orders');
    return saved ? JSON.parse(saved) : [];
  });
  const [notifySubscriptions, setNotifySubscriptions] = useState(() => {
    const saved = localStorage.getItem('agamiz_notify_subs');
    return saved ? JSON.parse(saved) : {};
  });

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [notifyProduct, setNotifyProduct] = useState(null);

  useEffect(() => {
    const disableImages = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
      }
    };
    document.addEventListener('dragstart', disableImages);
    return () => document.removeEventListener('dragstart', disableImages);
  }, []);

  // ── NEW: Persist auth/orders/subscriptions ─────────────────────────────
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('agamiz_user', JSON.stringify(currentUser));
    } else {
      localStorage.removeItem('agamiz_user');
    }
  }, [currentUser]);

  useEffect(() => {
    if (authToken) {
      localStorage.setItem('agamiz_auth_token', authToken);
    } else {
      localStorage.removeItem('agamiz_auth_token');
    }
  }, [authToken]);

  useEffect(() => {
    if (ageToken) {
      localStorage.setItem('agamiz_age_token', ageToken);
    } else {
      localStorage.removeItem('agamiz_age_token');
    }
  }, [ageToken]);

  useEffect(() => {
    if (!authToken) return;
    apiRequest('/api/auth/me', {token: authToken})
        .then(({user}) => {
          setCurrentUser(user);
        })
        .catch(() => {
          // IF THE BACKEND FAILS, IT DELETES THE SAVED DATA AND LOGS YOU OUT
          setAuthToken('');
          setCurrentUser(null);
        });
  }, [authToken]);

  useEffect(() => {
    if (!authToken || !currentUser) return;

    let cancelled = false;
    apiRequest('/api/orders', {token: authToken})
        .then(({orders: savedOrders}) => {
          if (!cancelled) setOrders(savedOrders);
        })
        .catch(() => {
          if (!cancelled) setOrders([]);
        });

    return () => {
      cancelled = true;
    };
  }, [authToken, currentUser]);

  useEffect(() => {
    localStorage.setItem('agamiz_orders', JSON.stringify(orders));
  }, [orders]);

  useEffect(() => {
    localStorage.setItem('agamiz_notify_subs', JSON.stringify(notifySubscriptions));
  }, [notifySubscriptions]);

  useEffect(() => {
    if (!authToken) return;

    let cancelled = false;
    apiRequest('/api/notifications/subscriptions', {token: authToken})
        .then(({subscriptions}) => {
          if (cancelled || !subscriptions?.length) return;

          const synced = {};
          subscriptions.forEach((sub) => {
            synced[sub.productId] = [
              ...(synced[sub.productId] || []),
              {id: sub.id, contact: sub.contact, timestamp: new Date(sub.createdAt).getTime()},
            ];
          });
          setNotifySubscriptions((prev) => ({...prev, ...synced}));
        })
        .catch(() => {
        });

    return () => {
      cancelled = true;
    };
  }, [authToken]);

  function CustomLangSwitcher({lang, setLang, theme, isOpen, setIsOpen}) {
    const languages = [
      {code: 'he', label: '🇮🇱 עברית'},
      {code: 'en', label: '🇺🇸 English'},
      {code: 'ru', label: '🇷🇺 Русский'}
    ];

    return (
        <div style={{position: 'relative'}}>
          <div
              className="nav-action-btn"
              onClick={() => setIsOpen(!isOpen)}
              style={{
                padding: '8px 16px',
                borderRadius: '10px',
                cursor: 'pointer',
                backgroundColor: theme.factorBg,
                color: theme.text,
                border: `1px solid ${theme.border}`,
                userSelect: 'none'
              }}
          >
            {languages.find(l => l.code === lang).label} ▼
          </div>

          {isOpen && (
              <div className="lang-dropdown" style={{
                position: 'absolute', top: '110%', right: 0,
                backgroundColor: theme.cardBg, border: `1px solid ${theme.border}`,
                borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                zIndex: 1000, overflow: 'hidden', minWidth: '120px'
              }}>
                {languages.map(l => (
                    <div
                        key={l.code}
                        className="lang-option"
                        onClick={() => {
                          setLang(l.code);
                          setIsOpen(false);
                        }}
                        style={{
                          padding: '10px 16px', cursor: 'pointer',
                          backgroundColor: lang === l.code ? theme.badgeBg : 'transparent',
                          color: theme.text,
                          transition: '0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = theme.badgeBg}
                        onMouseOut={(e) => e.target.style.backgroundColor = lang === l.code ? theme.badgeBg : 'transparent'}
                    >
                      {l.label}
                    </div>
                ))}
              </div>
          )}
        </div>
    );
  }

  const {products: liveProducts} = useProducts();

  useEffect(() => {
    setNotifySubscriptions((prev) => {
      const next = {...prev};
      let changed = false;

      liveProducts.filter((product) => isProductInStock(product)).forEach((product) => {
        if (next[product.id]) {
          delete next[product.id];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [liveProducts]);

  useEffect(() => {
    localStorage.setItem('agamiz_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('agamiz_lang', lang);
  }, [lang]);

  const handleVerifyAge = async () => {
    try {
      const data = await apiRequest('/api/age/verify', {
        method: 'POST',
        body: {confirmed: true},
      });
      setAgeToken(data.token);
      setIsAgeVerified(true);
    } catch (error) {
      alert(strings.ageServerError || error.message);
    }
  };

  const cycleTheme = () => {
    const order = ['light', 'dark', 'rugged'];
    const nextIndex = (order.indexOf(themeMode) + 1) % order.length;
    setThemeMode(order[nextIndex]);
  };

  const addToCart = (product) => {
    if (!isProductInStock(product)) return;
    setCart((prevCart) => {
      // ✅ FIX 2: dedupe by lineKey so the same product with a different type
      // creates a NEW line, while the same product + type increments qty.
      const dedupeKey = product.lineKey || String(product.id);
      const existingItem = prevCart.find((item) => (item.lineKey || String(item.id)) === dedupeKey);

      if (existingItem) {
        return prevCart.map((item) =>
            (item.lineKey || String(item.id)) === dedupeKey
                ? {...item, quantity: item.quantity + 1}
                : item
        );
      }
      return [...prevCart, {...product, quantity: 1}];
    });
  };

  const removeFromCart = (productIdOrKey) => {
    setCart((prevCart) => prevCart.filter((item) => (item.lineKey || String(item.id)) !== productIdOrKey));
  };

  const updateCartQuantity = (productIdOrKey, delta) => {
    setCart((prevCart) => prevCart
        .map((item) => ((item.lineKey || String(item.id)) === productIdOrKey ? { ...item, quantity: item.quantity + delta } : item))
        .filter((item) => item.quantity > 0));
  };

  // ── NEW: Auth Handlers ─────────────────────────────────────────────────
  const handleLogin = async (email, password, captchaToken) => {
    try {
      const data = await apiRequest('/api/auth/login', {
        method: 'POST',
        ageToken,
        body: { email, password, captchaToken },
      });
      setAuthToken(data.token);
      setCurrentUser(data.user);
      setShowAuthModal(false);
      return null;
    } catch (error) {
      const msg = error.message || '';
      if (msg.toLowerCase().includes('verify')) return 'emailNotVerified';
      if (msg.toLowerCase().includes('invalid email or password')) return 'loginError';
      return msg || 'loginError';
    }
  };

  const handleRegister = async (name, email, password, { acceptTerms, marketingOptIn, captchaToken }) => {
    try {
      await apiRequest('/api/auth/register', {
        method: 'POST',
        ageToken,
        body: { name, email, password, acceptTerms, marketingOptIn, captchaToken },
      });
      return 'verificationEmailSent';
    } catch (error) {
      return error.message.toLowerCase().includes('registered') ? 'registerError' : error.message;
    }
  };

  const handleForgotPassword = async (email, captchaToken) => {
    await apiRequest('/api/auth/forgot-password', {
      method: 'POST',
      body: { email, captchaToken },
    });
    return 'resetLinkSent';
  };

  const handleResendVerification = async (email, captchaToken) => {
    await apiRequest('/api/auth/resend-verification', {
      method: 'POST',
      body: { email, captchaToken },
    });
    return 'resendVerificationSent';
  };

  const handleLogout = () => { setAuthToken(''); setCurrentUser(null); setIsProfileOpen(false); };

  const handleDeleteAccount = async (password) => {
    const token = authToken || getStoredAuthToken();
    await apiRequest('/api/auth/account', {
      method: 'DELETE',
      token,
      body: { password },
    });
    setAuthToken('');
    setCurrentUser(null);
    setOrders([]);
    setNotifySubscriptions({});
    setIsProfileOpen(false);
    localStorage.removeItem('agamiz_orders');
    localStorage.removeItem('agamiz_notify_subs');
  };

  const handleUnsubscribeAlert = async (subscriptionId, productId, contact) => {
    const token = authToken || getStoredAuthToken();

    if (subscriptionId && token) {
      await apiRequest(`/api/notifications/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
        token,
      });
    } else if (productId && contact) {
      await apiRequest('/api/notifications/unsubscribe', {
        method: 'POST',
        token: token || undefined,
        body: { productId, contact },
      });
    } else {
      throw new Error('Unable to unsubscribe — please sign in again');
    }

    setNotifySubscriptions((prev) => {
      const next = { ...prev };
      if (next[productId]) {
        const filtered = next[productId].filter(
            (s) => s.id !== subscriptionId && s.contact !== contact
        );
        if (filtered.length) next[productId] = filtered;
        else delete next[productId];
      }
      return next;
    });
  };

  const handleNotifySubscribe = async (productId, contact, captchaToken) => {
    const data = await apiRequest('/api/notifications/subscribe', {
      method: 'POST',
      token: authToken || undefined,
      body: { productId, contact, captchaToken },
    });

    setNotifySubscriptions((prev) => ({
      ...prev,
      [productId]: [...(prev[productId] || []), {
        id: data.subscription?.id,
        contact,
        timestamp: Date.now(),
      }],
    }));

    return data;
  };
  // ── NEW: Order Persistence & Reorder ──────────────────────────────────
  const saveOrder = async (orderData) => {
    const order = {
      id: `ord_${Date.now().toString(36)}${Math.random().toString(36).substr(2, 3)}`,
      date: new Date().toLocaleDateString(),
      userEmail: currentUser?.email || 'guest',
      ...orderData,
    };
    setOrders(prev => [order, ...prev]);
    if (authToken && currentUser) {
      try {
        const data = await apiRequest('/api/orders', {
          method: 'POST',
          token: authToken,
          body: orderData,
        });
        setOrders(prev => [data.order, ...prev.filter(o => o.id !== order.id)]);
        if (orderData.paypalEmail) {
          setCurrentUser(prev => ({ ...prev, paypalEmail: orderData.paypalEmail }));
        }
        if (data.token) setAuthToken(data.token);
      } catch (error) {
        console.error('Order saved locally but failed on server:', error);
      }
    }
  };

  const handleReorder = (order) => {
    if (order.type === 'cart') {
      (order.items || []).forEach(item => {
        const product = liveProducts.find(p => p.id === item.id);
        if (product && isProductInStock(product)) addToCart(product);
      });
      setIsProfileOpen(false);
      setIsCartOpen(true);
    } else if (order.type === 'instant') {
      const product = liveProducts.find(p => p.id === order.product?.id);
      if (product && isProductInStock(product)) { setInstantBuyProduct(product); setIsProfileOpen(false); }
    }
  };

  const totalItemsCount = cart.reduce((total, item) => total + item.quantity, 0);
  const cartSubtotal = cart
      .reduce((total, item) => total + parseFloat(item.price) * item.quantity, 0);

  const deliveryFee = cart.reduce((total, item) => {
    if (requiresDeliveryFee(item)) {
      return total + (DELIVERY_FEE * item.quantity);
    }
    return total;
  }, 0);

  const cartTotalPrice = (cartSubtotal + deliveryFee).toFixed(2);

  const t = themes[themeMode];
  const strings = translations[lang];

  if (!isApiReady) {
    return (
        <div style={{ ...styles.loadingOverlay, backgroundColor: t.bg }}>
          <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
          <div style={{
            ...styles.spinner,
            borderColor: t.border,
            borderTopColor: t.accent
          }}></div>
          <p style={{ ...styles.loadingText, color: t.textMuted }}>Connecting to secure servers...</p>
        </div>
    );
  }

  return (
      <BrowserRouter basename="/">
        {/* Global root tag applied for contextual CSS */}
        <div dir={strings.dir} className={`app-root ${isMobile ? 'is-mobile' : 'is-desktop'}`} style={{ ...styles.appContainer, backgroundColor: t.bg, color: t.text }}>

          {!isAgeVerified && (
              <AgeVerificationModal theme={t} strings={strings} onVerify={handleVerifyAge} />
          )}

          <nav className="site-nav" style={{ ...styles.navbar, backgroundColor: t.cardBg, borderBottom: `1px solid ${t.border}` }}>
            <div className="nav-content" style={styles.navContent}>
              <Link to="/" style={styles.logoLink}>
                <h1 style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
                  <img src={logo} alt="Agamiz Logo" style={styles.logo} draggable="false" />
                </h1>
              </Link>

              <div className="nav-links" style={styles.navLinks}>
                <NavLink to="/" theme={t}>{strings.home}</NavLink>
                <NavLink to="/shop" theme={t}>{strings.shop}</NavLink>
                <NavLink to="/news" theme={t}>{strings.news}</NavLink>
                <NavLink to="/about" theme={t}>{strings.aboutUs}</NavLink>
              </div>

              <div className="nav-actions" style={styles.navActions}>
                <CustomLangSwitcher
                    lang={lang}
                    setLang={setLang}
                    theme={t}
                    isOpen={isLangMenuOpen}
                    setIsOpen={setIsLangMenuOpen}
                />

                <button
                    className="nav-action-btn"
                    style={{ ...styles.themeToggle, backgroundColor: t.factorBg, color: t.text }}
                    onClick={cycleTheme}
                    title="Toggle UI Theme Layout"
                >
                  {t.name}
                </button>

                {/* ── NEW: Profile / Sign-In Button ── */}
                {currentUser ? (
                    <button
                        className="nav-action-btn"
                        style={{ ...styles.cartNavButton, backgroundColor: '#7c3aed', color: '#fff', border: 'none' }}
                        onClick={() => { setIsProfileOpen(true); setIsCartOpen(false); }}
                        title={strings.myProfile}
                    >
                      <span style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '800', flexShrink: 0 }}>
                        {currentUser.name.charAt(0).toUpperCase()}
                      </span>
                      <span style={styles.cartText}>{currentUser.name.split(' ')[0]}</span>
                    </button>
                ) : (
                    <button
                        className="nav-action-btn"
                        style={{ ...styles.cartNavButton, backgroundColor: t.factorBg, color: t.text, border: `1px solid ${t.border}` }}
                        onClick={() => setShowAuthModal(true)}
                    >
                      <span>👤</span>
                      <span style={styles.cartText}>{strings.signIn}</span>
                    </button>
                )}

                <button className="nav-action-btn" style={{ ...styles.cartNavButton, backgroundColor: t.factorBg, color: t.text }} onClick={() => setIsCartOpen(true)}>
                  <span>🛒</span>
                  <span style={styles.cartText}>{strings.cart}</span>
                  {totalItemsCount > 0 && <span key={totalItemsCount} className="cart-badge-pop" style={{...styles.cartBadge, backgroundColor: t.accent}}>{totalItemsCount}</span>}
                </button>
              </div>
            </div>
          </nav>

          <div style={styles.mainContent}>
            <Routes>
              <Route path="/" element={<HomePage theme={t} strings={strings} products={liveProducts} isMobile={isMobile} />} />
              <Route path="/shop" element={<ShopPage products={liveProducts} addToCart={addToCart} onInstantBuy={setInstantBuyProduct} theme={t} strings={strings} onNotifyMe={setNotifyProduct} notifySubscriptions={notifySubscriptions} />} />
              <Route path="/shop/:productId" element={<ProductDetailsPage products={liveProducts} addToCart={addToCart} onInstantBuy={setInstantBuyProduct} theme={t} strings={strings} onNotifyMe={setNotifyProduct} notifySubscriptions={notifySubscriptions} />} />
              <Route path="/news" element={<NewsGalleryPage theme={t} strings={strings} currentLang={lang} products={liveProducts} />} />
              <Route path="/about" element={<AboutPage theme={t} strings={strings} />} />
              <Route path="/faq" element={<FAQPage theme={t} strings={strings} />} />
              <Route path="/privacy" element={<PrivacyPolicyPage theme={t} strings={strings} />} />
              <Route path="/terms" element={<TermsOfServicePage theme={t} strings={strings} />} />
              <Route path="/verify-email" element={<VerifyEmailPage theme={t} />} />
              <Route path="/reset-password" element={<ResetPasswordPage theme={t} strings={strings} captchaConfig={captchaConfig} themeMode={themeMode} />} />
              <Route path="/unsubscribe-marketing" element={<UnsubscribeMarketingPage theme={t} strings={strings} />} />
              <Route path="/admin" element={<AdminPage theme={t} strings={strings} products={liveProducts} />} />
              <Route path="*" element={<NotFoundPage theme={t} strings={strings} />} />
            </Routes>
          </div>

          <footer style={{ ...styles.footer, backgroundColor: t.cardBg, borderTop: `1px solid ${t.border}` }}>
            <div className="footer-content" style={styles.footerContent}>
              <div>
                <p style={{ ...styles.copyright, color: t.textMuted }}>
                  &copy; {new Date().getFullYear()} {strings.copyright}
                </p>
              </div>
              <div>
                <p style={{ ...styles.warning, color: "#ff0000" }}>
                  {strings.warning}
                </p>
              </div>
              <div style={styles.footerRight}>
                <a href="mailto:support@old.agamiz.com" style={{...styles.footerEmail, color: t.accent}}>support@old.agamiz.com</a>
                <span style={{ color: t.border }}>•</span>
                <Link to="/faq" style={{ ...styles.footerLink, color: t.textMuted }}>{strings.faq}</Link>
                <span style={{ color: t.border }}>•</span>
                <Link to="/privacy" style={{ ...styles.footerLink, color: t.textMuted }}>{strings.privacy}</Link>
                <span style={{ color: t.border }}>•</span>
                <Link to="/terms" style={{ ...styles.footerLink, color: t.textMuted }}>{strings.terms}</Link>
              </div>
            </div>
          </footer>

          {instantBuyProduct && (() => {
            const itemPrice = parseFloat(instantBuyProduct.price);
            const hasFee = itemPrice < DELIVERY_FEE_PRICE_THRESHOLD && !isSnusCategory(instantBuyProduct);
            const fee = hasFee ? DELIVERY_FEE : 0;
            const total = (itemPrice + fee).toFixed(2);

            return (
                <div className="overlay-backdrop" style={styles.overlay} onClick={() => setInstantBuyProduct(null)}>
                  <div className="modal-panel" style={{ ...styles.modal, backgroundColor: t.cardBg, borderColor: t.border }} onClick={(e) => e.stopPropagation()}>
                    <div style={styles.modalHeader}>
                      <h3 style={{ ...styles.drawerTitle, color: t.text }}>{strings.instantCheckout}</h3>
                      <button className="close-btn-animated" style={styles.closeButton} onClick={() => setInstantBuyProduct(null)}>✕</button>
                    </div>
                    <div style={styles.modalBody}>
                      <div style={styles.instantProductRow}>
                        <img src={productImages[instantBuyProduct.image]} alt={instantBuyProduct.name} style={styles.cartItemThumb} />
                        <div style={{ marginLeft: strings.dir === 'ltr' ? '14px' : '0', marginRight: strings.dir === 'rtl' ? '14px' : '0' }}>
                          <h4 style={{ ...styles.cartItemName, color: t.text }}>
                            {instantBuyProduct.name}
                            {instantBuyProduct.selectedType ? ` · ${instantBuyProduct.selectedType}` : ''}
                          </h4>
                          <p style={{ ...styles.cartItemMeta, color: t.textMuted }}>{strings.directChannel}</p>
                        </div>
                      </div>

                      <div style={{ marginTop: '20px' }}>
                        <div style={{ ...styles.totalSummaryRow, color: t.text, marginBottom: '8px' }}>
                          <span style={{ fontSize: '14px', color: t.textMuted, fontWeight: '600' }}>{strings.subtotal}</span>
                          <span style={{ ...styles.totalPrice, color: t.text, fontSize: '16px' }}> ₪{itemPrice.toFixed(2)} ILS</span>
                        </div>
                        {hasFee && (
                            <div style={{ ...styles.totalSummaryRow, color: t.text, marginBottom: '8px' }}>
                              <span style={{ fontSize: '14px', color: t.textMuted, fontWeight: '600' }}>{strings.deliveryFee}</span>
                              <span style={{ ...styles.totalPrice, color: t.text, fontSize: '16px' }}> ₪{fee.toFixed(2)} ILS</span>
                            </div>
                        )}
                        <hr style={{ ...styles.divider, borderTop: `1px solid ${t.border}`, margin: '12px 0' }} />
                        <div style={{ ...styles.totalSummaryRow, color: t.text, marginBottom: 0 }}>
                          <span style={{ fontSize: '16px', fontWeight: '800' }}>{strings.amountDue}</span>
                          <span style={{ ...styles.totalPrice, color: t.accent, fontSize: '22px' }}> ₪{total} ILS</span>
                        </div>
                      </div>

                      <hr style={{ ...styles.divider, borderTop: `1px solid ${t.border}`, margin: '20px 0' }} />
                      <div className="cart-scroll" style={{ overflowY: 'auto', maxHeight: '560px', paddingRight: '4px' }}>
                        <PayPalScriptProvider options={{ "client-id": CLIENT_ID, currency: "ILS", intent: "capture" }}>
                          <PayPalButtons
                              key={`${instantBuyProduct.id}-${instantBuyProduct.selectedType || 'base'}`}
                              style={{ layout: "vertical", shape: "pill", color: themeMode === 'rugged' ? "silver" : "gold" }}
                              createOrder={(data, actions) => {
                                const desc = instantBuyProduct.selectedType
                                    ? `Instant Purchase: ${instantBuyProduct.name} (${instantBuyProduct.selectedType})`
                                    : `Instant Purchase: ${instantBuyProduct.name}`;

                                return actions.order.create({
                                  purchase_units: [{
                                    description: desc,
                                    amount: { currency_code: "ILS", value: total }
                                  }]
                                });
                              }}
                              onApprove={(data, actions) => {
                                return actions.order.capture().then(async (details) => {
                                  await saveOrder({
                                    type: 'instant',
                                    product: {
                                      id: instantBuyProduct.id,
                                      name: instantBuyProduct.name + (instantBuyProduct.selectedType ? ` (${instantBuyProduct.selectedType})` : ''),
                                      price: instantBuyProduct.price,
                                      image: instantBuyProduct.image,
                                      selectedType: instantBuyProduct.selectedType || null,
                                    },
                                    total: total,
                                    paypalEmail: details.payer?.email_address,
                                  });
                                  alert(`Transaction Approved! Thank you ${details.payer.name.given_name}.`);
                                  setInstantBuyProduct(null);
                                });
                              }}
                          />
                        </PayPalScriptProvider>
                      </div>
                    </div>
                  </div>
                </div>
            );
          })()}

          {isCartOpen && (
              <div className="overlay-backdrop" style={styles.overlay} onClick={() => setIsCartOpen(false)}>
                <div className={`drawer-panel ${strings.dir === 'rtl' ? 'drawer-panel--from-left' : ''}`} style={{ ...styles.drawer, backgroundColor: t.cardBg, right: strings.dir === 'ltr' ? 0 : 'auto', left: strings.dir === 'rtl' ? 0 : 'auto' }} onClick={(e) => e.stopPropagation()}>

                  {/* Accent top bar */}
                  <div className="drawer-accent-bar" style={{ height: '3px', background: `linear-gradient(90deg, ${t.accent}, ${t.industrialAccent})`, flexShrink: 0 }} />

                  <div className="drawer-header-content" style={{ ...styles.drawerHeader, borderBottom: `1px solid ${t.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <h3 style={{ ...styles.drawerTitle, color: t.text }}>{strings.cartTitle}</h3>
                      {cart.length > 0 && (
                          <span style={{ backgroundColor: t.accent, color: '#fff', borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: '800', letterSpacing: '0.3px' }}>
                          {totalItemsCount}
                        </span>
                      )}
                    </div>
                    <button className="close-btn-animated" style={styles.closeButton} onClick={() => setIsCartOpen(false)}>✕</button>
                  </div>

                  <div className="cart-scroll" style={styles.drawerBody}>
                    {cart.length === 0 ? (
                        <div style={{ ...styles.emptyCart, color: t.textMuted }}>
                          <p className="empty-state-bounce" style={{ fontSize: "52px", margin: "0 0 14px 0" }}>🛍️</p>
                          <p style={{ fontSize: '15px', fontWeight: '700', color: t.text, margin: '0 0 6px 0' }}>{strings.cartEmpty}</p>
                          <p style={{ fontSize: '13px', color: t.textMuted, margin: 0 }}>Browse the shop and add items to get started.</p>
                        </div>
                    ) : (
                        cart.map((item, idx) => (
                            <div key={item.lineKey || item.id} className="cart-item" style={{ ...styles.cartItemRow, borderBottom: `1px solid ${t.border}`, animationDelay: `${idx * 0.05}s` }}>
                              <div style={{ position: 'relative', flexShrink: 0 }}>
                                <img src={productImages[item.image]} alt={item.name} style={{ ...styles.cartItemThumb, border: `1px solid ${t.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }} />
                                <span style={{ position: 'absolute', top: '-7px', right: '-7px', backgroundColor: t.accent, color: '#fff', borderRadius: '50%', width: '20px', height: '20px', fontSize: '10px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.35)', border: `2px solid ${t.cardBg}` }}>
                                  {item.quantity}
                                </span>
                              </div>
                              <div style={{ ...styles.cartItemInfo, marginLeft: strings.dir === 'ltr' ? '14px' : '0', marginRight: strings.dir === 'rtl' ? '14px' : '0' }}>
                                <h4 style={{ ...styles.cartItemName, color: t.text }}>{item.name}{item.selectedType ? ` · ${item.selectedType}` : ''}</h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <span style={{ fontSize: '11px', color: t.textMuted, fontWeight: '600' }}>{strings.cartQty}:</span>
                                  <button className="action-btn" onClick={() => updateCartQuantity(item.lineKey || item.id, -1)} style={{ width: '26px', height: '26px', borderRadius: '6px', border: `1px solid ${t.border}`, backgroundColor: t.factorBg, color: t.text, cursor: 'pointer', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>−</button>
                                  <span style={{ fontSize: '13px', fontWeight: '800', color: t.text, minWidth: '20px', textAlign: 'center' }}>{item.quantity}</span>
                                  <button className="action-btn" onClick={() => updateCartQuantity(item.lineKey || item.id, 1)} style={{ width: '26px', height: '26px', borderRadius: '6px', border: `1px solid ${t.border}`, backgroundColor: t.factorBg, color: t.text, cursor: 'pointer', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>+</button>
                                  <span style={{ fontSize: '12px', color: t.textMuted }}>× ₪{item.price}</span>
                                </div>
                                <span style={{ fontSize: '12px', fontWeight: '800', color: t.accent }}>₪{(parseFloat(item.price) * item.quantity).toFixed(2)}</span>
                              </div>
                              <button className="del-btn" style={{ ...styles.deleteItemBtn, width: '30px', height: '30px', borderRadius: '8px', backgroundColor: 'rgba(239,68,68,0.12)', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', flexShrink: 0 }} onClick={() => removeFromCart(item.lineKey || item.id)}>✕</button>
                            </div>
                        ))
                    )}
                  </div>

                  {cart.length > 0 && (
                      <div className="drawer-footer-reveal" style={{ ...styles.drawerFooter, backgroundColor: t.factorBg, borderTop: `1px solid ${t.border}` }}>
                        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                              type="checkbox"
                              id="localDelivery"
                              checked={localDeliveryEnabled}
                              onChange={(e) => setLocalDeliveryEnabled(e.target.checked)}
                              style={{ accentColor: t.accent, width: '18px', height: '18px' }}
                          />
                          <label htmlFor="localDelivery" style={{ color: t.textMuted, fontSize: '14px', cursor: 'pointer' }}>
                            {strings.localDelivery}
                          </label>
                        </div>

                        <div style={{ padding: '14px 16px', borderRadius: '12px', backgroundColor: t.cardBg, border: `1px solid ${t.border}`, marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: deliveryFee > 0 ? '8px' : 0 }}>
                            <span style={{ fontSize: '13px', color: t.textMuted, fontWeight: '600' }}>{strings.subtotal}</span>
                            <span style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>₪{cartSubtotal.toFixed(2)}</span>
                          </div>
                          {/*{localDeliveryEnabled && deliveryFee > 0 && (*/}
                          {/*    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>*/}
                          {/*      <span style={{ fontSize: '13px', color: t.textMuted, fontWeight: '600' }}>{strings.deliveryFee}</span>*/}
                          {/*      <span style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>₪{deliveryFee.toFixed(2)}</span>*/}
                          {/*    </div>*/}
                          {/*)}*/}
                          {/*{localDeliveryEnabled && deliveryFee > 0 && (*/}
                          {/*    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>*/}
                          {/*      <span style={{ fontSize: '13px', color: t.textMuted, fontWeight: '600' }}>{strings.deliveryFee}</span>*/}
                          {/*      <span style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>₪{deliveryFee.toFixed(2)}</span>*/}
                          {/*    </div>*/}
                          {/*)}*/}
                          <hr style={{ border: 0, borderTop: `1px solid ${t.border}`, margin: '8px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '14px', color: t.text, fontWeight: '800' }}>{strings.total}</span>
                            <span style={{ fontSize: '22px', fontWeight: '900', color: t.accent }}>₪{cartSubtotal.toFixed(2)}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: t.textMuted, marginTop: '4px', textAlign: strings.dir === 'rtl' ? 'left' : 'right' }}>ILS</div>
                        </div>
                        <hr style={{ ...styles.divider, borderTop: `1px solid ${t.border}` }} />

                        <div className="cart-scroll" style={{ overflowY: 'auto', maxHeight: '550px', paddingRight: '4px' }}>
                          <PayPalScriptProvider options={{ "client-id": CLIENT_ID, currency: "ILS", intent: "capture" }}>
                            <PayPalButtons
                                key={cartTotalPrice}
                                style={{ layout: "vertical", shape: "pill", color: themeMode === 'light' ? "gold" : "blue" }}
                                createOrder={(data, actions) => {
                                  const desc = instantBuyProduct.selectedType
                                      ? `Instant Purchase: ${instantBuyProduct.name} (${instantBuyProduct.selectedType})`
                                      : `Instant Purchase: ${instantBuyProduct.name}`;
                                  return actions.order.create({
                                    purchase_units: [{
                                      description: `Agamiz Purchase (${totalItemsCount} items)`,
                                      amount: { currency_code: "ILS", value: cartTotalPrice }
                                    }]
                                  });
                                }}
                                onApprove={(data, actions) => {
                                  return actions.order.capture().then(async (details) => {
                                    await saveOrder({
                                      type: 'cart',
                                      items: cart.map(item => ({
                                        id: item.id,
                                        name: item.name + (item.selectedType ? ` (${item.selectedType})` : ''),
                                        price: item.price,
                                        quantity: item.quantity,
                                        image: item.image,
                                        selectedType: item.selectedType || null,
                                        lineKey: item.lineKey,
                                      })),
                                      total: cartTotalPrice,
                                      paypalEmail: details.payer?.email_address,
                                    });
                                    alert(`Order Complete! Thank you ${details.payer.name.given_name}.`);
                                    setCart([]);
                                    setIsCartOpen(false);
                                  });
                                }}
                            />
                          </PayPalScriptProvider>
                        </div>
                      </div>
                  )}
                </div>
              </div>
          )}

          {/* ── NEW: Notify Me Modal ── */}
          {notifyProduct && (
              <NotifyMeModal
                  product={notifyProduct}
                  theme={t}
                  strings={strings}
                  themeMode={themeMode}
                  captchaConfig={captchaConfig}
                  defaultContact={currentUser?.email || ''}
                  onSubscribe={handleNotifySubscribe}
                  isSubscribed={!!(notifySubscriptions[notifyProduct.id]?.length)}
                  onClose={() => setNotifyProduct(null)}
              />
          )}

          {/* ── NEW: Auth Modal ── */}
          {showAuthModal && (
              <AuthModal
                  theme={t}
                  strings={strings}
                  themeMode={themeMode}
                  captchaConfig={captchaConfig}
                  onLogin={handleLogin}
                  onRegister={handleRegister}
                  onForgotPassword={handleForgotPassword}
                  onResendVerification={handleResendVerification}
                  onClose={() => setShowAuthModal(false)}
              />
          )}

          {/* ── NEW: Profile Drawer ── */}
          {isProfileOpen && currentUser && (
              <ProfileDrawer
                  theme={t}
                  strings={strings}
                  currentUser={currentUser}
                  orders={orders.filter(o => o.userEmail === currentUser.email)}
                  liveProducts={liveProducts}
                  notifySubscriptions={notifySubscriptions}
                  onLogout={handleLogout}
                  onDeleteAccount={handleDeleteAccount}
                  onUnsubscribeAlert={handleUnsubscribeAlert}
                  onReorder={handleReorder}
                  onClose={() => setIsProfileOpen(false)}
              />
          )}
        </div>
      </BrowserRouter>
  );
}

function AgeVerificationModal({ theme, strings, onVerify }) {
  const handleUnderageClick = () => window.location.href = "https://www.google.com";
  return (
      <div className="age-overlay-backdrop" style={styles.ageOverlay}>
        <div className="age-modal age-modal-panel" style={{ ...styles.ageModal, backgroundColor: theme.cardBg, borderColor: theme.border }}>
          <div className="age-icon-float" style={styles.ageIcon}>🔞</div>
          <h2 style={{ ...styles.ageTitle, color: theme.text }}>{strings.ageRequired}</h2>
          <p style={{ ...styles.ageSubtitle, color: theme.textMuted }}>{strings.ageNotice}</p>
          <hr style={{ ...styles.divider, borderTop: `1px solid ${theme.border}`, margin: '20px 0' }} />
          <div style={styles.ageActionsColumn}>
            <button style={{ ...styles.actionBtn, backgroundColor: theme.accent, color: "#fff", width: '100%', padding: '14px' }} onClick={onVerify}>
              {strings.ageVerify}
            </button>
            <button style={{ ...styles.ageCancelBtn, color: theme.textMuted }} onClick={handleUnderageClick}>
              {strings.ageLeave}
            </button>
          </div>
        </div>
      </div>
  );
}

function NavLink({ to, children, theme }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
      <Link to={to} style={{ ...styles.navLink, color: isActive ? theme.industrialAccent : theme.textMuted, fontWeight: isActive ? "700" : "500" }}>
        {children}
      </Link>
  );
}

/* ==========================================
   STYLISH HIGH-PERFORMANCE HOME PAGE COMPONENT
   ========================================== */
function HomePage({ theme, strings, products, isMobile }) {
  const navigate = useNavigate();
  const spotlightProducts = useMemo(() => products.slice(0, 3), [products]);

  return (
      <div className="fade-in-up" style={styles.homeContainer}>
        {/* Modern Industrial Hero Banner Panel */}
        <div className="premium-hero-card" style={{ ...styles.premiumHeroCard, backgroundColor: theme.cardBg, borderColor: theme.border }}>
          <div style={styles.meshPatternBg} />
          <span style={{ ...styles.heroBadge, backgroundColor: theme.badgeBg, color: theme.badgeText }}>
            {strings.welcome}
          </span>
          <h2 className="hero-title" style={{ ...styles.heroTitle, color: theme.text }}>{strings.heroTitle}</h2>
          <p className="hero-subtitle" style={{ ...styles.heroSubtitle, color: theme.textMuted }}>{strings.heroSubtitle}</p>

          <div style={styles.bannerImageFrame}>
            <img src={lostMaryBanner} alt="Agamiz Banner" style={styles.lostmarybanner} />
          </div>

          <p style={{ ...styles.heroNoticeText, color: theme.industrialAccent }}>
            {strings.bannerNotice}
          </p>

          <Link to="/shop" className="hover-scale" style={{ ...styles.heroButton, backgroundColor: theme.accent }}>
            {strings.startShopping}
          </Link>
        </div>

        {/* Dynamic Interactive Showroom Grid */}
        <div style={styles.showroomSection}>
          <h3 style={{ ...styles.showroomTitle, color: theme.text }}>⚡ Featured Collections</h3>
          <div className="showroom-grid" style={styles.showroomGrid}>
            {spotlightProducts.map((p, idx) => (
                <div
                    key={p.id}
                    className="hover-scale card-container stagger-in"
                    style={{ ...styles.showroomCard, backgroundColor: theme.factorBg, borderColor: theme.border, animationDelay: `${idx * 0.1}s` }}
                    onClick={() => navigate(`/shop/${p.id}`)}
                >
                  <img src={productImages[p.image]} alt={p.name} style={styles.showroomThumb} />
                  <div style={styles.showroomCardMeta}>
                    <h4 style={{ color: theme.text, margin: '0 0 4px 0', fontSize: '15px' }}>{p.name}</h4>
                    <span style={{ color: theme.industrialAccent, fontWeight: '700', fontSize: '14px' }}>₪ {p.price}</span>
                  </div>
                </div>
            ))}
          </div>
        </div>
      </div>
  );
}

/* ==========================================
   NEW NEWSPAGE & IMAGES FEED COMPONENT
   ========================================== */
function NewsGalleryPage({ theme, strings, currentLang, products }) {
  return (
      <div className="fade-in-up" style={styles.newsPageContainer}>
        <div style={styles.pageHeader}>
          <h2 style={{ ...styles.storeTitle, color: theme.text }}>{strings.latestNews}</h2>
          <p style={{ ...styles.storeSubtitle, color: theme.textMuted }}>{strings.descriptionNews}</p>
        </div>

        {/* Interactive Image Stream / Gallery Showcase */}
        <h3 style={{ ...styles.sectionDividerHeader, color: theme.text }}>{strings.exploreGallery}</h3>
        <div className="custom-scrollbar" style={styles.imageStreamContainer}>
          {products.map((p) => (
              <div key={`gallery-${p.id}`} className="hover-scale" style={{ ...styles.streamImageFrame, borderColor: theme.border, backgroundColor: theme.cardBg }}>
                <img src={productImages[p.image]} alt={p.name} style={styles.streamImg} />
                <div style={{ ...styles.streamOverlay, backgroundColor: theme.factorBg }}>
                  <span style={{ color: theme.text, fontSize: '12px', fontWeight: 'bold' }}>{p.name}</span>
                </div>
              </div>
          ))}
        </div>

        {/* Structured Corporate News Articles Layout */}
        <div className="news-grid" style={styles.newsGrid}>
          {newsData.map((article) => {
            const title = currentLang === 'he' ? article.titleHe : currentLang === 'ru' ? article.titleRu : article.titleEn;
            const desc = currentLang === 'he' ? article.descHe : currentLang === 'ru' ? article.descRu : article.descEn;
            return (
                <div key={article.id} style={{ ...styles.newsCard, backgroundColor: theme.cardBg, borderColor: theme.border }}>
                  <div style={styles.newsImageWrapper}>
                    <img src={article.image} alt={title} style={styles.newsCardImage} />
                  </div>
                  <div style={styles.newsContentBlock}>
                  <span style={{ ...styles.newsDateBadge, color: theme.industrialAccent }}>
                    {strings.publishedOn} {article.date}
                  </span>
                    <h4 style={{ ...styles.newsArticleTitle, color: theme.text }}>{title}</h4>
                    <p style={{ ...styles.newsArticleDesc, color: theme.textMuted }}>{desc}</p>
                  </div>
                </div>
            );
          })}
        </div>
      </div>
  );
}

function TrustBar({ theme, strings }) {
  const badges = [
    { icon: '🔞', label: strings.trustAge },
    { icon: '⚠️', label: strings.trustNicotine },
    { icon: '💳', label: strings.trustPaypal },
    { icon: '🚀', label: strings.trustDelivery },
  ];

  return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', marginBottom: '24px' }}>
        {badges.map((badge) => (
            <span key={badge.label} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '20px', backgroundColor: theme.factorBg, border: `1px solid ${theme.border}`, fontSize: '12px', fontWeight: '700', color: theme.text }}>
          {badge.icon} {badge.label}
        </span>
        ))}
      </div>
  );
}

function ShopPage({ products, addToCart, onInstantBuy, theme, strings, onNotifyMe, notifySubscriptions }) {
  const navigate = useNavigate();
  const [filterType, setFilterType] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const isNotified = (productId) => !!(notifySubscriptions[productId]?.length);

  const filteredProducts = useMemo(() => {
    let result = Array.isArray(products) ? products : [];

    if (filterType !== 'All') {
      result = result.filter(p => {
        if (p.category && String(p.category).toLowerCase() === filterType.toLowerCase()) {
          return true;
        }

        if (filterType === 'High Capacity') {
          const puffsValue = p.specs?.Puffs || p.specs?.puffs || p.puffs;
          if (puffsValue) {
            const numbersOnly = String(puffsValue).replace(/[^0-9]/g, '');
            return parseInt(numbersOnly, 10) >= 10000;
          }
        } else if (filterType === 'Disposable') {
          const desc = (p.description || '').toLowerCase();
          const modes = p.specs?.Modes || p.specs?.modes || [];

          const hasTurboMode = Array.isArray(modes)
              ? modes.some(m => String(m).toLowerCase().includes('disposable'))
              : String(modes).toLowerCase().includes('disposable');

          return desc.includes('disposable') || hasTurboMode;
        } else if (filterType === 'Grinder') {
          const desc = (p.description || '').toLowerCase();
          const modes = p.specs?.Modes || p.specs?.modes || [];

          const hasGrinderMode = Array.isArray(modes)
              ? modes.some(m => String(m).toLowerCase().includes('grinder'))
              : String(modes).toLowerCase().includes('grinder');

          return desc.includes('grinder') || hasGrinderMode;
        } else if (filterType === 'Rolling Paper') {
          const desc = (p.description || '').toLowerCase();
          const modes = p.specs?.Modes || p.specs?.modes || [];

          const hasRollingPaperMode = Array.isArray(modes)
              ? modes.some(m => String(m).toLowerCase().includes('rolling paper'))
              : String(modes).toLowerCase().includes('rolling paper');

          return desc.includes('rolling paper') || hasRollingPaperMode;
        } else if (filterType === 'Bongs') {
          const desc = (p.description || '').toLowerCase();
          const modes = p.specs?.Modes || p.specs?.modes || [];

          const hasBongMode = Array.isArray(modes)
              ? modes.some(m => String(m).toLowerCase().includes('bongs'))
              : String(modes).toLowerCase().includes('bongs');

          return desc.includes('bongs') || hasBongMode;
        } else if (filterType === 'Snus') {
          const desc = (p.description || '').toLowerCase();
          const modes = p.specs?.Modes || p.specs?.modes || [];

          const hasSnusMode = Array.isArray(modes)
              ? modes.some(m => String(m).toLowerCase().includes('snus'))
              : String(modes).toLowerCase().includes('snus');

          return desc.includes('snus') || hasSnusMode;
        }

        return false;
      });
    }

    if (searchQuery.trim() !== '') {
      const normalizedQuery = searchQuery.toLowerCase();
      result = result.filter(p =>
          (p.name || '').toLowerCase().includes(normalizedQuery) ||
          (p.description || '').toLowerCase().includes(normalizedQuery)
      );
    }

    return result;
  }, [products, filterType, searchQuery]);
  const filterLabels = {
    'All': strings.filterAll,
    'Rolling Paper': strings.filterRollingPaper,
    'Disposable': strings.filterTurbo,
    'Grinders': strings.filterGrinder,
    'Bongs': strings.filterBong,
    'Snus': strings.filterSnus
  };
  return (
      <div className="fade-in-up">
        <div style={styles.pageHeader}>
          <h2 style={{ ...styles.storeTitle, color: theme.text }}>{strings.vapes}</h2>
          <p style={{ ...styles.storeSubtitle, color: theme.textMuted }}>{strings.chooseVapes}</p>
          <br/>
          <TrustBar theme={theme} strings={strings} />

          <div style={styles.searchContainer}>
            <input
                type="text"
                placeholder={strings.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  ...styles.searchInput,
                  backgroundColor: theme.cardBg,
                  color: theme.text,
                  borderColor: theme.border
                }}
            />
            {searchQuery && (
                <button
                    onClick={() => setSearchQuery('')}
                    style={{ ...styles.searchClearBtn, color: theme.textMuted }}
                >
                  ✕
                </button>
            )}
          </div>

          <div style={styles.filterContainer}>
            {['All', 'Disposable', 'Rolling Paper', 'Bongs', 'Snus', 'Grinders'].map(f => (
                <button
                    key={f}
                    onClick={() => setFilterType(f)}
                    style={{
                      ...styles.filterBtn,
                      backgroundColor: filterType === f ? theme.accent : theme.factorBg,
                      color: filterType === f ? "#fff" : theme.text,
                      borderColor: theme.border
                    }}
                >
                  {/* If the key matches exactly, it will display the correct localized string */}
                  {filterLabels[f] || strings.filterAll}
                </button>
            ))}
          </div>
        </div>

        <div className="shop-grid" style={styles.grid}>
          {filteredProducts.map((product, index) => (
              <div key={product.id} className={`card-container stagger-in ${isProductInStock(product) ? "hover-scale" : ""}`} style={{ ...styles.card, backgroundColor: theme.cardBg, borderColor: theme.border, animationDelay: `${index * 0.08}s`, opacity: isProductInStock(product) ? 1 : 0.82 }} onClick={() => navigate(`/shop/${product.id}`)}>
                <div style={styles.imageContainer}>
                  <img src={productImages[product.image]} alt={product.name} style={{...styles.image, userSelect: 'none', filter: isProductInStock(product) ? 'none' : 'grayscale(80%)'}} draggable="false" />
                  {product.badge && <span style={styles.badge}>{product.badge}</span>}
                  {!isProductInStock(product) && (
                      <div style={{ ...styles.soldOutOverlay, backgroundColor: 'rgba(15,15,15,0.88)', color: '#ffffff', borderColor: 'rgba(255,255,255,0.25)' }}>
                        🚫 {strings.soldOut}
                      </div>
                  )}
                </div>
                <div style={styles.detailsContainer}>
                  <h3 style={{ ...styles.productName, color: theme.text }}>{product.name}</h3>
                  <p style={{ ...styles.productDescription, color: theme.textMuted }}>{product.description}</p>
                  <div style={styles.priceRow}>
                    <div style={styles.priceTag}>
                      <span style={{ ...styles.currency, color: theme.text }}>₪</span>
                      <span style={{ ...styles.price, color: theme.text }}>{product.price}</span>
                    </div>
                  </div>
                  <div className="action-row" style={styles.actionRow} onClick={(e) => e.stopPropagation()}>
                    {isProductInStock(product) ? (
                        <>
                          <button
                              className="action-btn"
                              style={{ ...styles.actionBtn, backgroundColor: theme.accent, color: "#ffffff", cursor: "pointer" }}
                              onClick={() => onInstantBuy(product)}
                          >
                            {strings.buyNow}
                          </button>
                          <button
                              className="action-btn"
                              style={{ ...styles.actionBtn, backgroundColor: theme.factorBg, color: theme.text, cursor: "pointer" }}
                              onClick={() => addToCart(product)}
                          >
                            {strings.addCart}
                          </button>
                        </>
                    ) : (
                        <button
                            className={isNotified(product.id) ? '' : 'notify-pulse'}
                            style={{ ...styles.actionBtn, width: '100%', backgroundColor: isNotified(product.id) ? '#059669' : '#7c3aed', color: '#ffffff', cursor: isNotified(product.id) ? 'default' : 'pointer', border: 'none' }}
                            onClick={() => !isNotified(product.id) && onNotifyMe(product)}
                            disabled={isNotified(product.id)}
                        >
                          {isNotified(product.id) ? strings.notifyAlready : strings.notifyMe}
                        </button>
                    )}
                  </div>
                </div>
              </div>
          ))}
        </div>
      </div>
  );
}

function ProductDetailsPage({ products, addToCart, onInstantBuy, theme, strings, onNotifyMe, notifySubscriptions }) {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [dailyPuffs, setDailyPuffs] = useState(200);
  const isNotified = (productId) => !!(notifySubscriptions[productId]?.length);

  const [selectedType, setSelectedType] = useState(null);

  const product = products.find((item) => String(item.id) === String(productId));

  // ✅ FIX 7: depend on a stable identity (productId), not the polled object reference.
  // Reset to the first available type whenever the product changes.
  useEffect(() => {
    if (product?.types?.length > 0) {
      setSelectedType(product.types[0]);
    } else {
      setSelectedType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, product?.types?.length]);

  // Guard clause
  if (!product) {
    return (
        <div style={{ ...styles.pageHero, padding: "60px 20px" }}>
          <h2 style={{ color: theme.text }}>{strings.productMissing}</h2>
          <p style={{ color: theme.textMuted }}>{strings.configNotFound}</p>
          <button onClick={() => navigate('/shop')} style={{ ...styles.heroButton, backgroundColor: theme.accent }}>{strings.returnShop}</button>
        </div>
    );
  }

  // ✅ FIX 1 + 6: type-aware stock/price computation.
  const hasTypes = Array.isArray(product.types) && product.types.length > 0;
  const allTypesOut = hasTypes && product.types.every((t) => t.soldOut);

  // Pick the active type, falling back to the first non-sold-out type if possible.
  const activeType =
      selectedType ||
      (hasTypes ? product.types.find((t) => !t.soldOut) || product.types[0] : null);

  const effectivePrice = activeType ? activeType.price : product.price;
  const effectiveSoldOut = hasTypes ? (activeType ? activeType.soldOut : allTypesOut) : product.soldOut;
  const inStock = !effectiveSoldOut;

  const puffsValue = product.specs?.Puffs || product.specs?.puffs;
  const totalPuffs = puffsValue ? parseInt(String(puffsValue).replace(/[^0-9]/g, ''), 10) : 0;
  const estimatedDays = totalPuffs > 0 ? Math.floor(totalPuffs / dailyPuffs) : 0;

  // ✅ FIX 2: build a unique cart line key including the selected type
  const buildVariant = (baseProduct) => {
    const variantName = activeType ? activeType.name : null;
    return {
      ...baseProduct,
      price: effectivePrice,
      soldOut: effectiveSoldOut,
      // The line key used for cart dedupe. "id::type" prevents type collisions.
      lineKey: baseProduct.id + (variantName ? `::${variantName}` : ''),
      selectedType: variantName,
    };
  };

  const handleInstantBuy = () => {
    const variant = buildVariant(product);
    onInstantBuy(variant);
  };

  const handleAddToCart = () => {
    const variant = buildVariant(product);
    addToCart(variant);
  };

  return (
      <div className="fade-in-up" style={{ ...styles.detailsPageLayout, backgroundColor: theme.cardBg, borderColor: theme.border }}>
        <button onClick={() => navigate('/shop')} style={{ ...styles.backButton, color: theme.textMuted }}>{strings.backCatalog}</button>
        <div className="details-flex-container" style={styles.detailsFlexContainer}>
          <div className="details-image-wrapper" style={{ ...styles.detailsImageWrapper, backgroundColor: theme.factorBg }}>
            <img src={productImages[product.image]} alt={product.name} style={{ ...styles.detailsLargeImage, filter: inStock ? 'none' : 'grayscale(70%)' }} />
            {product.badge && <span style={styles.badge}>{product.badge}</span>}
            {!inStock && (
                <div style={{ ...styles.soldOutOverlay, backgroundColor: 'rgba(15,15,15,0.88)', color: '#ffffff', borderColor: 'rgba(255,255,255,0.25)' }}>
                  🚫 {strings.soldOut}
                </div>
            )}
          </div>

          <div style={styles.detailsInfoPanel}>
            <span style={{ ...styles.heroBadge, backgroundColor: theme.badgeBg, color: theme.badgeText, width: 'fit-content' }}>{strings.verifiedHardware}</span>
            <h2 className="detail-main-title" style={{ ...styles.detailMainTitle, color: theme.text }}>{product.name}</h2>
            <div style={styles.detailPriceTag}>
              <span style={{ ...styles.currency, color: theme.text, fontSize: '18px' }}>₪</span>
              <span style={{ ...styles.price, color: theme.text, fontSize: '32px' }}>{effectivePrice}</span>
              <span style={{ color: theme.textMuted, fontSize: '14px', marginLeft: '8px', marginRight: '8px' }}>ILS</span>
              {/* ✅ FIX 4: surface the selected type next to the price */}
              {activeType && (
                  <span style={{
                    marginLeft: '10px',
                    padding: '4px 10px',
                    borderRadius: '8px',
                    backgroundColor: theme.badgeBg,
                    color: theme.badgeText,
                    fontSize: '12px',
                    fontWeight: '700',
                    letterSpacing: '0.3px',
                  }}>
                    {activeType.name}
                  </span>
              )}
            </div>
            <p style={{ ...styles.detailLongDescription, color: theme.textMuted }}>{product.longDescription || product.description}</p>

            {hasTypes && (
                <div style={{ marginBottom: '24px' }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '800', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {strings.chooseType || 'Choose variant'}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {product.types.map((t) => {
                      const isActive = activeType?.name === t.name;
                      const typeOut = t.soldOut;
                      return (
                          <button
                              key={t.name}
                              onClick={() => setSelectedType(t)}
                              disabled={typeOut}
                              className={isActive ? 'type-chip-active' : ''}
                              style={{
                                padding: '10px 14px',
                                borderRadius: '12px',
                                border: `2px solid ${isActive ? theme.accent : theme.border}`,
                                backgroundColor: isActive ? `${theme.accent}15` : theme.factorBg,
                                color: typeOut ? theme.textMuted : (isActive ? theme.accent : theme.text),
                                cursor: typeOut ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                fontWeight: '800',
                                opacity: typeOut ? 0.5 : 1,
                                transition: 'all 0.2s ease',
                                textDecoration: typeOut ? 'line-through' : 'none',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '2px',
                                // ✅ FIX 8: smaller min-width + flex grow so chips reflow on mobile
                                flex: '1 1 130px',
                                maxWidth: '200px',
                              }}
                          >
                            <span>{t.name}</span>
                            <span style={{ fontSize: '11px', color: isActive ? theme.accent : theme.textMuted, fontWeight: '700' }}>
                              ₪{t.price}
                            </span>
                            {typeOut && (
                                <span style={{ position: 'absolute', top: '-6px', right: '-6px', backgroundColor: '#ef4444', color: '#fff', fontSize: '9px', fontWeight: '800', padding: '2px 6px', borderRadius: '8px', letterSpacing: '0.4px' }}>
                                  {strings.soldOut}
                                </span>
                            )}
                          </button>
                      );
                    })}
                  </div>
                </div>
            )}

            {(product.containNicotine || product.category === 'Disposable') && (
                <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', marginBottom: '20px' }}>
                  <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: '#ef4444', lineHeight: '1.5' }}>
                    ⚠️ {strings.nicotineDisclaimer}
                  </p>
                </div>
            )}

            {product.specs && (
                <div style={{ ...styles.specificationsBox, backgroundColor: theme.factorBg, borderColor: theme.border, marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: theme.text, borderBottom: `1px solid ${theme.border}`, paddingBottom: '6px' }}>{strings.techSpecs}</h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <tbody>
                    {Object.entries(product.specs).map(([key, value]) => {
                      const icon = hardwareIcons[key] || "⚙️";
                      return (
                          <tr key={key} style={{ borderBottom: `1px solid ${theme.border}` }}>
                            <td style={{ padding: '8px 4px', fontWeight: '600', color: theme.text, width: '40%' }}>{icon} {key}</td>
                            <td style={{ padding: '8px 4px', color: theme.textMuted }}>{value}</td>
                          </tr>
                      );
                    })}
                    </tbody>
                  </table>
                </div>
            )}

            {totalPuffs > 0 && (
                <div style={{ ...styles.specificationsBox, backgroundColor: theme.factorBg, borderColor: theme.border }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: theme.text }}>{strings.calculatorTitle}</h4>
                  <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        width: '100%',
                        maxWidth: '320px',
                        padding: '12px 16px',
                        borderRadius: '16px',
                        background: theme.cardBackground || 'rgba(255, 255, 255, 0.03)',
                        backdropFilter: 'blur(8px)',
                        border: `1px solid ${theme.border || 'rgba(255, 255, 255, 0.08)'}`,
                        transition: 'all 0.3s ease'
                      }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: theme.textMuted, letterSpacing: '0.3px' }}>
                        {strings.calcInput}
                      </label>
                      <span
                          key={dailyPuffs}
                          style={{
                            fontSize: '14px',
                            fontWeight: '700',
                            background: theme.accentBackground || `${theme.accent}15`,
                            color: theme.accent,
                            padding: '2px 10px',
                            borderRadius: '20px',
                            minWidth: '36px',
                            textAlign: 'center',
                            animation: 'sliderPop 0.15s ease-out'
                          }}
                      >
        {dailyPuffs}
      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', height: '24px' }}>
                      <input
                          type="range"
                          min="50"
                          max="1000"
                          step="50"
                          value={dailyPuffs}
                          onChange={(e) => setDailyPuffs(Number(e.target.value))}
                          className="modern-slider-input"
                          style={{
                            background: `linear-gradient(to right, ${theme.accent} 0%, ${theme.accent} ${((dailyPuffs - 50) / (1000 - 50)) * 100}%, ${theme.border || 'rgba(255,255,255,0.1)'} ${((dailyPuffs - 50) / (1000 - 50)) * 100}%, ${theme.border || 'rgba(255,255,255,0.1)'} 100%)`,
                            '--thumb-color': theme.bg || '#ffffff',
                            '--thumb-border': theme.accent,
                            '--thumb-glow': `${theme.accent}33`
                          }}
                      />
                    </div>

                    <style>{`
    @keyframes sliderPop {
      0% { transform: scale(0.92); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
  `}</style>
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: theme.text }}>
                    {strings.calcResult} <strong style={{ color: theme.accent }}>~{estimatedDays} {strings.days}</strong>
                  </div>
                </div>
            )}

            <div className="action-row" style={{ ...styles.actionRow, marginTop: '30px', maxWidth: '400px' }}>
              {inStock ? (
                  <>
                    <button className="action-btn" style={{ ...styles.actionBtn, backgroundColor: theme.accent, color: "#ffffff", padding: '16px', cursor: "pointer" }} onClick={handleInstantBuy}>
                      {strings.buyNowInstant}
                    </button>
                    <button className="action-btn" style={{ ...styles.actionBtn, backgroundColor: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, padding: '16px', cursor: "pointer" }} onClick={handleAddToCart}>
                      {strings.addShoppingCart}
                    </button>
                  </>
              ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', maxWidth: '400px' }}>
                    <div style={{ padding: '14px 20px', borderRadius: '10px', backgroundColor: theme.factorBg, border: `2px dashed ${theme.border}`, color: theme.textMuted, fontWeight: '700', fontSize: '14px', textAlign: 'center' }}>
                      🚫 {strings.soldOut}
                    </div>
                    <button
                        className={isNotified(product.id) ? '' : 'notify-pulse'}
                        style={{ ...styles.actionBtn, backgroundColor: isNotified(product.id) ? '#059669' : '#7c3aed', color: '#fff', cursor: isNotified(product.id) ? 'default' : 'pointer', padding: '16px', fontSize: '15px', border: 'none' }}
                        onClick={() => !isNotified(product.id) && onNotifyMe(product)}
                        disabled={isNotified(product.id)}
                    >
                      {isNotified(product.id) ? strings.notifyAlready : strings.notifyMe}
                    </button>
                  </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
}


/* ==========================================
   NEW: NOTIFY ME MODAL COMPONENT
   ========================================== */
function NotifyMeModal({ product, theme, strings, themeMode, captchaConfig, defaultContact, onSubscribe, isSubscribed, onClose }) {
  const [contact, setContact] = useState(defaultContact || '');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const handleSubmit = async () => {
    if (!contact.trim() || isSubmitting) return;
    if (captchaConfig.enabled && !captchaToken) {
      setError(strings.captchaRequired);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onSubscribe(product.id, contact.trim(), captchaToken);
      setSubmitted(true);
    } catch (submitError) {
      setError(submitError.message || strings.notifyError);
      setCaptchaToken('');
      setCaptchaResetKey((k) => k + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const success = submitted || isSubscribed;

  return (
      <div className="overlay-backdrop" style={styles.overlay} onClick={onClose}>
        <div className="modal-panel" style={{ ...styles.modal, backgroundColor: theme.cardBg, borderColor: theme.border, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h3 style={{ ...styles.drawerTitle, color: theme.text }}>🔔 {strings.notifyMe.replace('🔔 ', '')}</h3>
            <button className="close-btn-animated" style={styles.closeButton} onClick={onClose}>✕</button>
          </div>
          <div className="drawer-accent-bar" style={{ height: '3px', borderRadius: '2px', background: 'linear-gradient(90deg, #f59e0b, #fbbf24)', marginBottom: '20px' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px', borderRadius: '14px', backgroundColor: theme.factorBg, border: `1px solid ${theme.border}`, marginBottom: '20px' }}>
            <img src={productImages[product.image]} alt={product.name} style={{ width: '56px', height: '56px', objectFit: 'contain', borderRadius: '10px', border: `1px solid ${theme.border}` }} />
            <div>
              <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: theme.text, fontSize: '14px' }}>{product.name}</p>
              <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted }}>{strings.notifyDesc}</p>
            </div>
          </div>

          {success ? (
              <div className="success-pop" style={{ textAlign: 'center', padding: '24px 0' }}>
                <p style={{ fontSize: '48px', margin: '0 0 12px 0' }}>🎉</p>
                <p style={{ color: '#10b981', fontWeight: '700', fontSize: '15px', margin: 0 }}>{strings.notifySuccess}</p>
              </div>
          ) : (
              <div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                      type="text"
                      value={contact}
                      onChange={e => setContact(e.target.value)}
                      placeholder={strings.notifyInput}
                      onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                      disabled={isSubmitting}
                      style={{ ...styles.authInput, flex: 1, backgroundColor: theme.factorBg, color: theme.text, borderColor: theme.border }}
                  />
                  <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      style={{ padding: '12px 18px', borderRadius: '12px', backgroundColor: isSubmitting ? '#d97706' : '#f59e0b', color: '#fff', border: 'none', cursor: isSubmitting ? 'wait' : 'pointer', fontWeight: '700', fontSize: '14px', whiteSpace: 'nowrap', flexShrink: 0, opacity: isSubmitting ? 0.85 : 1 }}
                  >
                    {isSubmitting ? strings.notifySubmitting : strings.notifySubscribe}
                  </button>
                </div>
                {captchaConfig.enabled && (
                    <CaptchaWidget
                        siteKey={captchaConfig.siteKey}
                        themeMode={themeMode}
                        onToken={setCaptchaToken}
                        resetKey={captchaResetKey}
                    />
                )}
                {error && (
                    <p style={{ margin: '10px 0 0 0', color: '#ef4444', fontSize: '13px', fontWeight: '600' }}>{error}</p>
                )}
              </div>
          )}
        </div>
      </div>
  );
}

function VerifyEmailPage({ theme }) {
  const location = useLocation();
  const [state, setState] = useState({
    tone: 'loading',
    title: 'Verifying your email',
    message: 'Please wait while we confirm your Agamiz account.',
  });

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('token');
    if (!token) {
      setState({
        tone: 'error',
        title: 'Verification link missing',
        message: 'The email verification link does not include a token. Please use the latest link from your email.',
      });
      return;
    }

    apiRequest('/api/auth/verify-email', {
      method: 'POST',
      body: { token },
    })
        .then(() => setState({
          tone: 'success',
          title: 'Email verified',
          message: 'Your account is ready. You can now sign in and continue shopping.',
        }))
        .catch((error) => setState({
          tone: 'error',
          title: 'Verification failed',
          message: error.message,
        }));
  }, [location.search]);

  const isSuccess = state.tone === 'success';
  const isLoading = state.tone === 'loading';
  const accent = isSuccess ? '#10b981' : isLoading ? theme.accent : '#ef4444';

  return (
      <div className="fade-in-up" style={{ minHeight: '58vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 20px' }}>
        <div style={{
          width: '100%',
          maxWidth: '540px',
          borderRadius: '20px',
          border: `1px solid ${theme.border}`,
          backgroundColor: theme.cardBg,
          boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
          overflow: 'hidden',
          textAlign: 'center'
        }}>
          <div style={{ height: '4px', background: `linear-gradient(90deg, ${accent}, ${theme.industrialAccent})` }} />
          <div style={{ padding: '38px 32px 34px' }}>
            <div style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              margin: '0 auto 22px',
              border: `2px solid ${accent}`,
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              fontWeight: '900',
              boxShadow: `0 0 0 8px ${accent}18`
            }}>
              {isLoading ? <span className="verify-spinner" /> : isSuccess ? '✓' : '!'}
            </div>

            <p style={{ margin: '0 0 8px', color: accent, fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Account security
            </p>
            <h2 style={{ color: theme.text, fontSize: '30px', lineHeight: 1.15, margin: '0 0 12px', fontWeight: '900' }}>
              {state.title}
            </h2>
            <p style={{ color: theme.textMuted, fontSize: '15px', lineHeight: 1.7, margin: '0 auto 28px', maxWidth: '390px' }}>
              {state.message}
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/" style={{...styles.heroButton, backgroundColor: theme.accent, minWidth: '140px'}}>
                Return home
              </Link>
              <Link to="/shop" style={{
                ...styles.heroButton,
                backgroundColor: 'transparent',
                color: theme.text,
                border: `1px solid ${theme.border}`,
                minWidth: '140px'
              }}>
                Go to shop
              </Link>
            </div>
          </div>
        </div>
      </div>
  );
}

/* ==========================================
   NEW: AUTH MODAL COMPONENT (LOGIN / REGISTER)
   ========================================== */
function AuthModal({ theme, strings, themeMode, captchaConfig, onLogin, onRegister, onForgotPassword, onResendVerification, onClose }) {
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const resetCaptcha = () => {
    setCaptchaToken('');
    setCaptchaResetKey((k) => k + 1);
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError('');
    setSuccess('');
    setShowResendVerification(false);
    resetCaptcha();
  };

  const requireCaptchaOrError = () => {
    if (captchaConfig.enabled && !captchaToken) {
      setError(strings.captchaRequired);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setShowResendVerification(false);

    if (!email.trim()) return;
    if (!requireCaptchaOrError()) return;

    if (mode === 'forgot') {
      setIsLoading(true);
      try {
        const resultKey = await onForgotPassword(email.trim(), captchaToken);
        setSuccess(strings[resultKey] || resultKey);
        resetCaptcha();
      } catch (submitError) {
        setError(submitError.message);
        resetCaptcha();
      }
      setIsLoading(false);
      return;
    }

    if (!password.trim() || (mode === 'register' && !name.trim())) return;

    if (mode === 'register' && !acceptTerms) {
      setError(strings.acceptTermsError);
      return;
    }

    setIsLoading(true);
    const resultKey = mode === 'login'
        ? await onLogin(email.trim(), password, captchaToken)
        : await onRegister(name.trim(), email.trim(), password, { acceptTerms, marketingOptIn, captchaToken });

    if (resultKey === 'verificationEmailSent') {
      setSuccess(strings[resultKey] || resultKey);
      switchMode('login');
    } else if (resultKey === 'emailNotVerified') {
      setError(strings.emailNotVerified);
      setShowResendVerification(true);
      resetCaptcha();
    } else if (resultKey) {
      setError(strings[resultKey] || resultKey);
      resetCaptcha();
    } else {
      resetCaptcha();
    }
    setIsLoading(false);
  };

  const handleResend = async () => {
    if (!email.trim()) return;
    if (!requireCaptchaOrError()) return;
    setIsLoading(true);
    setError('');
    try {
      const resultKey = await onResendVerification(email.trim(), captchaToken);
      setSuccess(strings[resultKey] || resultKey);
      setShowResendVerification(false);
      resetCaptcha();
    } catch (submitError) {
      setError(submitError.message);
      resetCaptcha();
    }
    setIsLoading(false);
  };

  const title = mode === 'login' ? strings.signIn : mode === 'register' ? strings.signUp : strings.forgotPasswordTitle;

  // Localized UI Styles mapping to your active layout configurations
  const formStyles = {
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
      padding: '20px'
    },
    modal: {
      backgroundColor: theme.cardBg, border: `1px solid ${theme.border}`,
      borderRadius: themeMode === 'rugged' ? '0px' : '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      width: '100%', maxWidth: '440px', padding: '28px', position: 'relative',
      boxSizing: 'border-box'
    },
    header: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'
    },
    title: {
      margin: 0, fontSize: '22px', fontWeight: '800', color: theme.text, letterSpacing: '-0.02em'
    },
    closeBtn: {
      background: 'none', border: 'none', color: theme.textMuted, fontSize: '18px',
      cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center'
    },
    accentBar: {
      height: '3px', borderRadius: '2px',
      background: `linear-gradient(90deg, ${theme.accent}, ${theme.industrialAccent || theme.accent})`,
      marginBottom: '24px'
    },
    inputGroup: {
      display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px'
    },
    label: {
      fontSize: '12px', fontWeight: '700', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em'
    },
    input: {
      padding: '12px 14px', borderRadius: themeMode === 'rugged' ? '0px' : '10px',
      backgroundColor: theme.factorBg, color: theme.text, border: `1px solid ${theme.border}`,
      fontSize: '15px', outline: 'none', width: '100%', boxSizing: 'border-box', transition: 'border-color 0.2s'
    },
    checkboxLabel: {
      display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer',
      fontSize: '13px', color: theme.textMuted, lineHeight: '1.4', marginBottom: '10px'
    },
    submitBtn: {
      width: '100%', padding: '14px', borderRadius: themeMode === 'rugged' ? '0px' : '12px',
      backgroundColor: theme.accent, color: '#ffffff', border: 'none', fontWeight: '700',
      fontSize: '15px', cursor: isLoading ? 'wait' : 'opacity', opacity: isLoading ? 0.75 : 1,
      transition: 'all 0.2s ease', boxShadow: `0 4px 12px ${theme.accent}20`, marginTop: '8px'
    },
    switchBtn: {
      background: 'none', border: 'none', color: theme.industrialAccent || theme.accent,
      cursor: 'pointer', fontSize: '13px', fontWeight: '700', textDecoration: 'none',
      padding: '4px', textAlign: 'center', display: 'block', margin: '12px auto 0 auto'
    },
    forgotBtn: {
      background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer',
      fontSize: '12px', fontWeight: '600', display: 'inline-block', width: 'fit-content',
      alignSelf: 'flex-end', marginTop: '-10px', marginBottom: '12px'
    }
  };

  return (
      <div style={formStyles.overlay} onClick={onClose}>
        <div style={formStyles.modal} onClick={e => e.stopPropagation()}>
          <div style={formStyles.header}>
            <h3 style={formStyles.title}>{title}</h3>
            <button style={formStyles.closeBtn} onClick={onClose}>✕</button>
          </div>
          <div style={formStyles.accentBar} />

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {mode === 'forgot' && (
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: theme.textMuted, lineHeight: 1.5, textAlign: 'center' }}>
                  {strings.forgotPasswordDesc}
                </p>
            )}

            {mode === 'register' && (
                <div style={formStyles.inputGroup}>
                  <label style={formStyles.label}>{strings.accountName}</label>
                  <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder={strings.accountName}
                      style={formStyles.input}
                  />
                </div>
            )}

            <div style={formStyles.inputGroup}>
              <label style={formStyles.label}>{strings.accountEmail}</label>
              <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={formStyles.input}
              />
            </div>

            {mode !== 'forgot' && (
                <div style={formStyles.inputGroup}>
                  <label style={formStyles.label}>{strings.accountPassword}</label>
                  <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                      style={formStyles.input}
                  />
                </div>
            )}

            {mode === 'login' && (
                <button onClick={() => switchMode('forgot')} style={formStyles.forgotBtn}>
                  {strings.forgotPassword}
                </button>
            )}

            {mode === 'register' && (
                <div style={{ margin: '8px 0' }}>
                  <label style={formStyles.checkboxLabel}>
                    <input
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={e => setAcceptTerms(e.target.checked)}
                        style={{ accentColor: theme.accent, marginTop: '3px', flexShrink: 0 }}
                    />
                    <span>
                  {strings.acceptTermsPrefix}{' '}
                      <Link to="/terms" onClick={onClose} style={{ color: theme.accent, fontWeight: '700' }}>{strings.terms}</Link>
                      {' '}{strings.acceptTermsAnd}{' '}
                      <Link to="/privacy" onClick={onClose} style={{ color: theme.accent, fontWeight: '700' }}>{strings.privacy}</Link>
                </span>
                  </label>
                  <label style={formStyles.checkboxLabel}>
                    <input
                        type="checkbox"
                        checked={marketingOptIn}
                        onChange={e => setMarketingOptIn(e.target.checked)}
                        style={{ accentColor: theme.accent, marginTop: '3px', flexShrink: 0 }}
                    />
                    <span>{strings.marketingOptIn}</span>
                  </label>
                </div>
            )}

            {captchaConfig.enabled && (
                <div style={{ marginBottom: '16px' }}>
                  <CaptchaWidget
                      siteKey={captchaConfig.siteKey}
                      themeMode={themeMode}
                      onToken={setCaptchaToken}
                      resetKey={`${mode}-${captchaResetKey}`}
                  />
                </div>
            )}

            {error && (
                <p style={{ color: '#ef4444', backgroundColor: '#ef444410', border: '1px solid #ef444425', padding: '10px', borderRadius: '8px', fontSize: '13px', margin: '0 0 14px 0', textAlign: 'center', fontWeight: '600' }}>
                  {error}
                </p>
            )}

            {success && (
                <p style={{ color: '#10b981', backgroundColor: '#10b98110', border: '1px solid #10b98125', padding: '10px', borderRadius: '8px', fontSize: '13px', margin: '0 0 14px 0', textAlign: 'center', fontWeight: '700' }}>
                  {success}
                </p>
            )}

            {showResendVerification && (
                <button
                    onClick={handleResend}
                    disabled={isLoading}
                    style={{ ...formStyles.submitBtn, backgroundColor: theme.factorBg, color: theme.text, border: `1px solid ${theme.border}`, marginBottom: '10px' }}
                >
                  {strings.resendVerification}
                </button>
            )}

            <button onClick={handleSubmit} disabled={isLoading} style={formStyles.submitBtn}>
              {isLoading ? '...' : (mode === 'login' ? strings.signIn : mode === 'register' ? strings.createAccount : strings.sendResetLink)}
            </button>

            <button
                onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                style={formStyles.switchBtn}
            >
              {mode === 'forgot' ? strings.backToSignIn : (mode === 'login' ? strings.noAccount : strings.haveAccount)}
            </button>
          </div>
        </div>
      </div>
  );
}

/* ==========================================
   NEW: PROFILE DRAWER COMPONENT
   ========================================== */
function ProfileDrawer({ theme, strings, currentUser, orders, liveProducts, notifySubscriptions, onLogout, onDeleteAccount, onUnsubscribeAlert, onReorder, onClose }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [unsubscribingId, setUnsubscribingId] = useState(null);
  const [unsubscribeError, setUnsubscribeError] = useState('');

  const canReorder = (order) => {
    if (order.type === 'cart') return (order.items || []).some(item => isProductInStock(liveProducts.find(p => p.id === item.id)));
    return isProductInStock(liveProducts.find(p => p.id === order.product?.id));
  };

  const activeAlerts = Object.entries(notifySubscriptions || {}).flatMap(([productId, subs]) =>
      (subs || []).map((s) => ({
        ...s,
        alertKey: s.id || `${productId}-${s.contact}`,
        productId: Number(productId),
        productName: liveProducts.find((p) => p.id === Number(productId))?.name || `Product #${productId}`,
      }))
  );

  const handleUnsubscribe = async (alert) => {
    setUnsubscribeError('');
    setUnsubscribingId(alert.alertKey);
    try {
      await onUnsubscribeAlert(alert.id, alert.productId, alert.contact);
    } catch (error) {
      setUnsubscribeError(error.message || strings.notifyError);
    } finally {
      setUnsubscribingId(null);
    }
  };

  const handleDeleteSubmit = async () => {
    setDeleteError('');
    setIsDeleting(true);
    try {
      await onDeleteAccount(deletePassword);
    } catch (error) {
      setDeleteError(strings.deleteAccountError);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
      <div className="overlay-backdrop" style={styles.overlay} onClick={onClose}>
        <div className="drawer-panel drawer-panel--from-left" style={{ ...styles.drawer, backgroundColor: theme.cardBg, left: 0, right: 'auto' }} onClick={e => e.stopPropagation()}>
          {/* Purple accent bar */}
          <div className="drawer-accent-bar" style={{ height: '3px', background: 'linear-gradient(90deg, #7c3aed, #a78bfa)', flexShrink: 0 }} />

          {/* Header with avatar */}
          <div className="drawer-header-content" style={{ ...styles.drawerHeader, borderBottom: `1px solid ${theme.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: 'linear-gradient(135deg, #7c3aed, #a78bfa)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '18px', fontWeight: '900', flexShrink: 0 }}>
                {currentUser.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: '800', color: theme.text, fontSize: '15px' }}>{currentUser.name}</p>
                <p style={{ margin: 0, fontSize: '11px', color: theme.textMuted }}>{currentUser.email}</p>
              </div>
            </div>
            <button className="close-btn-animated" style={styles.closeButton} onClick={onClose}>✕</button>
          </div>

          {/* Body */}
          <div className="cart-scroll" style={styles.drawerBody}>
            {/* PayPal linked badge */}
            {currentUser.paypalEmail && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '12px', backgroundColor: theme.factorBg, border: `1px solid ${theme.border}`, marginBottom: '20px' }}>
                  <span style={{ fontSize: '22px' }}>💳</span>
                  <div>
                    <p style={{ margin: 0, fontSize: '11px', fontWeight: '800', color: theme.text, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{strings.paypalLinked}</p>
                    <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted }}>{currentUser.paypalEmail}</p>
                  </div>
                  <span style={{ marginLeft: 'auto', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', flexShrink: 0 }} />
                </div>
            )}

            {/* Payment note when no PayPal yet */}
            {!currentUser.paypalEmail && (
                <div style={{ padding: '12px 14px', borderRadius: '12px', backgroundColor: theme.factorBg, border: `1px dashed ${theme.border}`, marginBottom: '20px' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted }}>💳 {strings.savedPaymentNote}</p>
                </div>
            )}

            {/* Stock Alerts */}
            <h4 style={{ color: theme.text, margin: '0 0 14px 0', fontSize: '14px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🔔 {strings.stockAlerts}
              {activeAlerts.length > 0 && (
                  <span style={{ backgroundColor: '#f59e0b', color: '#fff', borderRadius: '20px', padding: '1px 8px', fontSize: '11px', fontWeight: '800' }}>{activeAlerts.length}</span>
              )}
            </h4>
            {activeAlerts.length === 0 ? (
                <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 24px 0' }}>{strings.noStockAlerts}</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                  {unsubscribeError && (
                      <p style={{ color: '#ef4444', fontSize: '12px', margin: '0 0 4px 0', fontWeight: '600' }}>{unsubscribeError}</p>
                  )}
                  {activeAlerts.map((alert) => (
                      <div key={alert.alertKey} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', backgroundColor: theme.factorBg, border: `1px solid ${theme.border}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{alert.productName}</p>
                          <p style={{ margin: 0, fontSize: '11px', color: theme.textMuted }}>{alert.contact}</p>
                        </div>
                        <button
                            onClick={() => handleUnsubscribe(alert)}
                            disabled={unsubscribingId === alert.alertKey}
                            style={{ padding: '6px 10px', borderRadius: '8px', backgroundColor: 'transparent', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', cursor: unsubscribingId === alert.alertKey ? 'wait' : 'pointer', fontSize: '11px', fontWeight: '700', flexShrink: 0, opacity: unsubscribingId === alert.alertKey ? 0.6 : 1 }}
                        >
                          {unsubscribingId === alert.alertKey ? '...' : strings.unsubscribe}
                        </button>
                      </div>
                  ))}
                </div>
            )}

            {/* Order History */}
            <h4 style={{ color: theme.text, margin: '0 0 14px 0', fontSize: '14px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📦 {strings.orderHistory}
              {orders.length > 0 && (
                  <span style={{ backgroundColor: '#7c3aed', color: '#fff', borderRadius: '20px', padding: '1px 8px', fontSize: '11px', fontWeight: '800' }}>{orders.length}</span>
              )}
            </h4>

            {orders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textMuted }}>
                  <p className="empty-state-bounce" style={{ fontSize: '44px', margin: '0 0 10px 0' }}>📭</p>
                  <p style={{ fontSize: '14px', margin: 0 }}>{strings.noOrders}</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {orders.map((order, orderIndex) => {
                    const label = order.type === 'cart'
                        ? (order.items || []).map(i => i.name).join(', ')
                        : order.product?.name || '';
                    const itemCount = order.type === 'cart' ? (order.items || []).reduce((s, i) => s + i.quantity, 0) : 1;
                    const reorderable = canReorder(order);

                    return (
                        <div key={order.id} className="profile-order-card" style={{ padding: '14px', borderRadius: '14px', backgroundColor: theme.factorBg, border: `1px solid ${theme.border}`, animationDelay: `${orderIndex * 0.07}s` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: reorderable ? '10px' : 0 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ margin: '0 0 3px 0', fontSize: '11px', color: theme.textMuted }}>
                                {strings.orderDate}: {order.date} · {itemCount} {strings.orderItems}
                              </p>
                              <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>
                                {label}
                              </p>
                            </div>
                            <span style={{ fontSize: '16px', fontWeight: '900', color: '#7c3aed', marginLeft: '10px', flexShrink: 0 }}>₪{order.total}</span>
                          </div>
                          {reorderable && (
                              <button
                                  onClick={() => onReorder(order)}
                                  style={{ width: '100%', padding: '8px', borderRadius: '8px', backgroundColor: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}
                              >
                                {strings.reorder}
                              </button>
                          )}
                        </div>
                    );
                  })}
                </div>
            )}
          </div>

          {/* Footer - Sign Out / Delete */}
          <div className="drawer-footer-reveal" style={{ ...styles.drawerFooter, backgroundColor: theme.factorBg, borderTop: `1px solid ${theme.border}` }}>
            <p style={{ margin: '0 0 12px 0', fontSize: '11px', color: theme.textMuted, textAlign: 'center' }}>
              {strings.profileTitle} · {currentUser.joinDate ? `Joined ${currentUser.joinDate}` : ''}
            </p>

            {showDeleteConfirm ? (
                <div style={{ marginBottom: '12px', padding: '14px', borderRadius: '12px', backgroundColor: theme.cardBg, border: '1px solid rgba(239,68,68,0.3)' }}>
                  <p style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '800', color: '#ef4444' }}>{strings.deleteAccountTitle}</p>
                  <p style={{ margin: '0 0 12px 0', fontSize: '11px', color: theme.textMuted, lineHeight: '1.5' }}>{strings.deleteAccountDesc}</p>
                  <input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder={strings.deleteAccountPassword}
                      onKeyDown={(e) => e.key === 'Enter' && handleDeleteSubmit()}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.border}`, backgroundColor: theme.factorBg, color: theme.text, fontSize: '13px', marginBottom: '10px', boxSizing: 'border-box' }}
                  />
                  {deleteError && <p style={{ color: '#ef4444', fontSize: '12px', margin: '0 0 8px 0', fontWeight: '600' }}>{deleteError}</p>}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); setDeleteError(''); }}
                        style={{ flex: 1, padding: '10px', borderRadius: '8px', backgroundColor: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}`, cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}
                    >
                      {strings.deleteAccountCancel}
                    </button>
                    <button
                        onClick={handleDeleteSubmit}
                        disabled={isDeleting || !deletePassword}
                        style={{ flex: 1, padding: '10px', borderRadius: '8px', backgroundColor: '#ef4444', color: '#fff', border: 'none', cursor: isDeleting ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '700', opacity: !deletePassword ? 0.6 : 1 }}
                    >
                      {isDeleting ? '...' : strings.deleteAccountConfirm}
                    </button>
                  </div>
                </div>
            ) : (
                <button
                    onClick={() => setShowDeleteConfirm(true)}
                    style={{ width: '100%', padding: '10px', borderRadius: '12px', backgroundColor: 'transparent', color: theme.textMuted, border: `1px dashed ${theme.border}`, cursor: 'pointer', fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}
                >
                  🗑️ {strings.deleteAccount}
                </button>
            )}

            <button
                onClick={onLogout}
                style={{ width: '100%', padding: '13px', borderRadius: '12px', backgroundColor: 'transparent', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', cursor: 'pointer', fontSize: '14px', fontWeight: '700' }}
            >
              🚪 {strings.signOut}
            </button>
          </div>
        </div>
      </div>
  );
}

function AboutPage({ theme, strings }) {
  return (
      <div className="fade-in-up" style={{ maxWidth: "800px", margin: "0 auto", padding: "40px 0" }}>
        {/* Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: '42px', color: theme.text, marginBottom: '16px', fontWeight: '900' }}>
            {strings.missionTitle}
          </h2>
          <div style={{ height: '4px', width: '60px', backgroundColor: theme.accent, margin: '0 auto 20px auto' }} />
          <p style={{ fontSize: '18px', color: theme.textMuted, lineHeight: '1.8' }}>
            {strings.missionDesc}
          </p>
        </div>

        {/* Feature Grid */}
        <div className="news-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          {[
            { title: "Fair Pricing", icon: "⚖️", desc: "We challenge local monopoly pricing by prioritizing customer equity." },
            { title: "Premium Hardware", icon: "💎", desc: "Verified, high-quality gear engineered for longevity and performance." },
            { title: "Rapid Logistics", icon: "🚀", desc: "Optimized same-day delivery services throughout Be'er Sheva." }
          ].map((item, idx) => (
              <div key={idx} style={{
                padding: '30px',
                borderRadius: '20px',
                backgroundColor: theme.cardBg,
                border: `1px solid ${theme.border}`,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '15px' }}>{item.icon}</div>
                <h4 style={{ color: theme.text, marginBottom: '10px' }}>{item.title}</h4>
                <p style={{ color: theme.textMuted, fontSize: '14px', lineHeight: '1.6' }}>{item.desc}</p>
              </div>
          ))}
        </div>
      </div>
  );
}

function FAQPage({ theme, strings }) {
  const faqItems = [
    { q: strings.faqAge, a: strings.faqAgeA },
    { q: strings.faqShipping, a: strings.faqShippingA },
    { q: strings.faqReturns, a: strings.faqReturnsA },
    { q: strings.faqPayment, a: strings.faqPaymentA },
    { q: strings.faqNicotine, a: strings.faqNicotineA },
  ];

  return (
      <div className="fade-in-up" style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
        <div className="premium-hero-card" style={{ ...styles.premiumHeroCard, backgroundColor: theme.cardBg, borderColor: theme.border, marginBottom: '40px' }}>
          <div style={styles.meshPatternBg} />
          <span style={{ ...styles.heroBadge, backgroundColor: theme.badgeBg, color: theme.badgeText, position: 'relative', zIndex: 1 }}>❓ FAQ</span>
          <h2 className="hero-title" style={{ ...styles.heroTitle, color: theme.text, position: 'relative', zIndex: 1 }}>{strings.faqTitle}</h2>
          <p className="hero-subtitle" style={{ ...styles.heroSubtitle, color: theme.textMuted, position: 'relative', zIndex: 1, marginBottom: 0 }}>Everything you need to know about shopping at Agamiz.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {faqItems.map((item, idx) => (
              <div key={idx} style={{ padding: '24px', borderRadius: '16px', backgroundColor: theme.cardBg, border: `1px solid ${theme.border}` }}>
                <h4 style={{ color: theme.industrialAccent, margin: '0 0 10px 0', fontSize: '16px', fontWeight: '800' }}>{item.q}</h4>
                <p style={{ color: theme.textMuted, fontSize: '14px', lineHeight: '1.7', margin: 0 }}>{item.a}</p>
              </div>
          ))}
        </div>
      </div>
  );
}

function PrivacyPolicyPage({ theme, strings }) {
  const sections = [
    { title: strings.privacyData, content: strings.privacyDataDesc },
    { title: strings.privacyUse, content: strings.privacyUseDesc },
    { title: strings.privacyDelete, content: strings.privacyDeleteDesc },
    { title: strings.privacyContact, content: strings.privacyContactDesc },
  ];

  return (
      <div className="fade-in-up" style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
        <div className="premium-hero-card" style={{ ...styles.premiumHeroCard, backgroundColor: theme.cardBg, borderColor: theme.border, marginBottom: '40px' }}>
          <div style={styles.meshPatternBg} />
          <span style={{ ...styles.heroBadge, backgroundColor: theme.badgeBg, color: theme.badgeText, position: 'relative', zIndex: 1 }}>🔒 Privacy</span>
          <h2 className="hero-title" style={{ ...styles.heroTitle, color: theme.text, position: 'relative', zIndex: 1 }}>{strings.privacyTitle}</h2>
          <p className="hero-subtitle" style={{ ...styles.heroSubtitle, color: theme.textMuted, position: 'relative', zIndex: 1, marginBottom: 0 }}>{strings.lastUpdated}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          {sections.map((section, idx) => (
              <div key={idx} style={{ paddingBottom: '20px', borderBottom: `1px solid ${theme.border}` }}>
                <h4 style={{ color: theme.industrialAccent, marginBottom: '10px', fontSize: '18px' }}>{section.title}</h4>
                <p style={{ color: theme.textMuted, fontSize: '15px', lineHeight: '1.7', margin: 0 }}>{section.content}</p>
              </div>
          ))}
        </div>
      </div>
  );
}

function TermsOfServicePage({ theme, strings }) {
  const termsSections = [
    {
      title: "1. Age Restriction & Compliance",
      content: "Use of this site and purchase of any nicotine-related products is strictly restricted to individuals aged 18 and older. By entering this site, you certify that you meet the legal age requirement in your jurisdiction."
    },
    {
      title: "2. Secure Transactions",
      content: strings.paypalArch
    },
    {
      title: "3. Delivery & Liability",
      content: "All local deliveries in Be'er Sheva are optimized for speed and safety. Agamiz is not liable for issues arising from third-party shipping once a package has been dispatched, though we strive to ensure every order reaches you in perfect condition."
    },
    {
      title: "4. Product Usage",
      content: "Products are provided for personal use only. Users assume all responsibility for the handling of electronic cigarettes and vaping hardware. Please follow manufacturer guidelines provided with each device to ensure safety."
    },
    {
      title: "5. Account Deletion",
      content: "You may permanently delete your account at any time from your profile. Deletion removes your personal data, order history, and stock alert subscriptions from our systems."
    },
    {
      title: "6. Nicotine & Health",
      content: strings.warning + " Products are intended for adult smokers and vapers aged 18+. Keep all vaping products out of reach of children and pets."
    }
  ];

  return (
      <div className="fade-in-up" style={{ maxWidth: "800px", margin: "0 auto", padding: "40px 20px" }}>
        <h2 style={{ ...styles.heroTitle, color: theme.text, marginBottom: "10px" }}>{strings.terms}</h2>
        <p style={{ color: theme.textMuted, fontSize: "14px", marginBottom: "40px" }}>{strings.lastUpdated}</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          {termsSections.map((section, idx) => (
              <div key={idx} style={{
                paddingBottom: '20px',
                borderBottom: `1px solid ${theme.border}`
              }}>
                <h4 style={{ color: theme.industrialAccent, marginBottom: '10px', fontSize: '18px' }}>{section.title}</h4>
                <p style={{ color: theme.textMuted, fontSize: '15px', lineHeight: '1.7', margin: 0 }}>
                  {section.content}
                </p>
              </div>
          ))}
        </div>
      </div>
  );
}

function ResetPasswordPage({ theme, strings, captchaConfig, themeMode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState({ tone: 'form', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const token = new URLSearchParams(location.search).get('token');

  const handleSubmit = async () => {
    if (password.length < 8) {
      setState({ tone: 'error', message: strings.accountPassword });
      return;
    }
    if (password !== confirm) {
      setState({ tone: 'error', message: strings.passwordsMismatch });
      return;
    }
    if (captchaConfig.enabled && !captchaToken) {
      setState({ tone: 'error', message: strings.captchaRequired });
      return;
    }

    setIsLoading(true);
    try {
      await apiRequest('/api/auth/reset-password', {
        method: 'POST',
        body: { token, password, captchaToken },
      });
      setState({ tone: 'success', message: strings.resetPasswordSuccess });
    } catch (error) {
      setState({ tone: 'error', message: error.message || strings.resetPasswordFailed });
      setCaptchaToken('');
      setCaptchaResetKey((k) => k + 1);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
        <div className="fade-in-up" style={{ ...styles.pageHero, padding: '60px 20px' }}>
          <h2 style={{ color: theme.text }}>{strings.resetPasswordFailed}</h2>
          <Link to="/" style={{ ...styles.heroButton, backgroundColor: theme.accent }}>{strings.returnShop}</Link>
        </div>
    );
  }

  return (
      <div className="fade-in-up" style={{ maxWidth: '480px', margin: '40px auto', padding: '0 20px' }}>
        <div style={{ padding: '32px', borderRadius: '20px', border: `1px solid ${theme.border}`, backgroundColor: theme.cardBg }}>
          <h2 style={{ ...styles.heroTitle, color: theme.text, fontSize: '28px', marginBottom: '8px' }}>{strings.resetPasswordTitle}</h2>
          {state.tone === 'success' ? (
              <>
                <p style={{ color: '#10b981', fontWeight: '700', marginBottom: '24px' }}>{state.message}</p>
                <button onClick={() => navigate('/')} style={{ ...styles.heroButton, backgroundColor: theme.accent, width: '100%' }}>{strings.signIn}</button>
              </>
          ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '700', color: theme.textMuted, display: 'block', marginBottom: '6px' }}>{strings.newPassword}</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={{ ...styles.authInput, width: '100%', boxSizing: 'border-box', backgroundColor: theme.factorBg, color: theme.text, borderColor: theme.border }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '700', color: theme.textMuted, display: 'block', marginBottom: '6px' }}>{strings.confirmPassword}</label>
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} style={{ ...styles.authInput, width: '100%', boxSizing: 'border-box', backgroundColor: theme.factorBg, color: theme.text, borderColor: theme.border }} />
                </div>
                {captchaConfig?.enabled && (
                    <CaptchaWidget
                        siteKey={captchaConfig.siteKey}
                        themeMode={themeMode}
                        onToken={setCaptchaToken}
                        resetKey={captchaResetKey}
                    />
                )}
                {state.tone === 'error' && <p style={{ color: '#ef4444', fontSize: '13px', margin: 0, fontWeight: '600' }}>{state.message}</p>}
                <button onClick={handleSubmit} disabled={isLoading} style={{ ...styles.heroButton, backgroundColor: '#7c3aed', width: '100%', opacity: isLoading ? 0.75 : 1, cursor: isLoading ? 'wait' : 'pointer' }}>
                  {isLoading ? '...' : strings.resetPassword}
                </button>
              </div>
          )}
        </div>
      </div>
  );
}

function UnsubscribeMarketingPage({ theme, strings }) {
  const location = useLocation();
  const [state, setState] = useState({ tone: 'loading', message: '' });

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('token');
    if (!token) {
      setState({ tone: 'error', message: strings.marketingUnsubFailed });
      return;
    }

    apiRequest('/api/auth/unsubscribe-marketing', { method: 'POST', body: { token } })
        .then(() => setState({ tone: 'success', message: strings.marketingUnsubscribed }))
        .catch((error) => setState({ tone: 'error', message: error.message || strings.marketingUnsubFailed }));
  }, [location.search, strings.marketingUnsubFailed, strings.marketingUnsubscribed]);

  const accent = state.tone === 'success' ? '#10b981' : state.tone === 'loading' ? theme.accent : '#ef4444';

  return (
      <div className="fade-in-up" style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
        <div style={{ maxWidth: '480px', width: '100%', padding: '36px', borderRadius: '20px', border: `1px solid ${theme.border}`, backgroundColor: theme.cardBg, textAlign: 'center' }}>
          {state.tone === 'loading' ? <span className="verify-spinner" style={{ color: accent }} /> : <p style={{ fontSize: '40px', margin: '0 0 16px 0' }}>{state.tone === 'success' ? '✓' : '!'}</p>}
          <p style={{ color: accent, fontWeight: '700', fontSize: '15px', margin: 0 }}>{state.tone === 'loading' ? '...' : state.message}</p>
          {state.tone !== 'loading' && (
              <Link to="/" style={{ ...styles.heroButton, backgroundColor: theme.accent, display: 'inline-block', marginTop: '24px' }}>{strings.home}</Link>
          )}
        </div>
      </div>
  );
}

function AdminPage({ theme, strings, products }) {
  const [isAuthed, setIsAuthed] = useState(false);
  const [secretInput, setSecretInput] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isChecking, setIsChecking] = useState(true);
  const [stats, setStats] = useState(null);
  const [adminProducts, setAdminProducts] = useState(products);
  const [stockMessage, setStockMessage] = useState('');
  const [broadcast, setBroadcast] = useState({ subject: '', title: '', message: '' });
  const [broadcastResult, setBroadcastResult] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [updatingStockId, setUpdatingStockId] = useState(null);

  const verifyAdmin = useCallback(async () => {
    const secret = getAdminSecret();
    if (!secret) {
      setIsAuthed(false);
      setIsChecking(false);
      return;
    }
    try {
      await apiRequest('/api/admin/verify', { adminSecret: secret });
      setIsAuthed(true);
      const [statsData, productsData] = await Promise.all([
        adminRequest('/api/admin/stats'),
        adminRequest('/api/admin/products'),
      ]);
      setStats(statsData);
      setAdminProducts(productsData.products || []);
    } catch {
      sessionStorage.removeItem('agamiz_admin_secret');
      setIsAuthed(false);
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => { verifyAdmin(); }, [verifyAdmin]);

  const handleLogin = async () => {
    setLoginError('');
    sessionStorage.setItem('agamiz_admin_secret', secretInput.trim());
    try {
      await apiRequest('/api/admin/verify', { adminSecret: secretInput.trim() });
      setIsAuthed(true);
      setSecretInput('');
      await verifyAdmin();
    } catch {
      sessionStorage.removeItem('agamiz_admin_secret');
      setLoginError(strings.adminInvalid);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('agamiz_admin_secret');
    setIsAuthed(false);
    setStats(null);
  };

  const toggleStock = async (product) => {
    setStockMessage('');
    setUpdatingStockId(product.id);
    try {
      const data = await adminRequest(`/api/admin/products/${product.id}/stock`, {
        method: 'PATCH',
        body: { soldOut: isProductInStock(product) },
      });
      setAdminProducts((prev) => prev.map((p) => (p.id === product.id ? data.product : p)));
      if (data.notifiedCount > 0) {
        setStockMessage(`${data.notifiedCount} ${strings.adminNotified}`);
      }
    } catch (error) {
      setStockMessage(error.message);
    } finally {
      setUpdatingStockId(null);
    }
  };

  const sendBroadcast = async () => {
    if (!broadcast.subject.trim() || !broadcast.title.trim() || !broadcast.message.trim()) {
      setBroadcastResult(strings.adminBroadcastEmpty);
      return;
    }
    setIsSending(true);
    setBroadcastResult('');
    try {
      const data = await adminRequest('/api/admin/broadcast', {
        method: 'POST',
        body: broadcast,
      });
      setBroadcastResult(`${strings.adminBroadcastSuccess}: ${data.sent}/${data.total} sent`);
      setBroadcast({ subject: '', title: '', message: '' });
      const statsData = await adminRequest('/api/admin/stats');
      setStats(statsData);
    } catch (error) {
      setBroadcastResult(error.message);
    } finally {
      setIsSending(false);
    }
  };

  if (isChecking) {
    return <div className="fade-in-up" style={{ textAlign: 'center', padding: '80px 20px', color: theme.textMuted }}>...</div>;
  }

  if (!isAuthed) {
    return (
        <div className="fade-in-up" style={{ maxWidth: '420px', margin: '60px auto', padding: '0 20px' }}>
          <div style={{ padding: '32px', borderRadius: '20px', border: `1px solid ${theme.border}`, backgroundColor: theme.cardBg }}>
            <h2 style={{ color: theme.text, margin: '0 0 8px 0', fontSize: '24px', fontWeight: '900' }}>{strings.adminLogin}</h2>
            <p style={{ color: theme.textMuted, fontSize: '13px', marginBottom: '20px' }}>{strings.adminTitle}</p>
            <label style={{ fontSize: '12px', fontWeight: '700', color: theme.textMuted, display: 'block', marginBottom: '6px' }}>{strings.adminSecretLabel}</label>
            <input type="password" value={secretInput} onChange={e => setSecretInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} style={{ ...styles.authInput, width: '100%', boxSizing: 'border-box', backgroundColor: theme.factorBg, color: theme.text, borderColor: theme.border, marginBottom: '12px' }} />
            {loginError && <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 12px 0' }}>{loginError}</p>}
            <button onClick={handleLogin} style={{ ...styles.heroButton, backgroundColor: '#7c3aed', width: '100%' }}>{strings.adminEnter}</button>
          </div>
        </div>
    );
  }

  return (
      <div className="fade-in-up" style={{ maxWidth: '900px', margin: '0 auto', padding: '30px 20px 60px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ color: theme.text, margin: 0, fontSize: '28px', fontWeight: '900' }}>⚙️ {strings.adminTitle}</h2>
          <button onClick={handleLogout} style={{ padding: '10px 16px', borderRadius: '10px', backgroundColor: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}`, cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>{strings.adminLogout}</button>
        </div>

        {stats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '28px' }}>
              {[
                { label: strings.adminProducts, value: stats.productCount },
                { label: strings.adminOrders, value: stats.orderCount },
                { label: strings.adminUsers, value: stats.userCount },
                { label: strings.adminSubscribers, value: stats.marketingSubscribers, accent: '#f59e0b' },
              ].map((item) => (
                  <div key={item.label} style={{ padding: '18px', borderRadius: '14px', backgroundColor: theme.cardBg, border: `1px solid ${theme.border}`, textAlign: 'center' }}>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: '900', color: item.accent || theme.accent }}>{item.value}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: theme.textMuted, fontWeight: '600' }}>{item.label}</p>
                  </div>
              ))}
            </div>
        )}

        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ color: theme.text, marginBottom: '16px', fontSize: '18px', fontWeight: '800' }}>📦 {strings.adminStock}</h3>
          {stockMessage && <p style={{ color: '#10b981', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>{stockMessage}</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {adminProducts.map((product) => (
                <div key={product.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '14px 16px', borderRadius: '12px', backgroundColor: theme.cardBg, border: `1px solid ${theme.border}`, flexWrap: 'wrap' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: '700', color: theme.text, fontSize: '14px' }}>{product.name}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: isProductInStock(product) ? '#10b981' : '#ef4444', fontWeight: '700' }}>
                      {isProductInStock(product) ? strings.adminInStock : strings.adminSoldOut}
                    </p>
                  </div>
                  <button
                      onClick={() => toggleStock(product)}
                      disabled={updatingStockId === product.id}
                      style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', cursor: updatingStockId === product.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '700', backgroundColor: isProductInStock(product) ? '#ef4444' : '#10b981', color: '#fff', opacity: updatingStockId === product.id ? 0.7 : 1 }}
                  >
                    {updatingStockId === product.id ? '...' : (isProductInStock(product) ? strings.adminMarkSoldOut : strings.adminMarkInStock)}
                  </button>
                </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '24px', borderRadius: '16px', backgroundColor: theme.cardBg, border: `1px solid ${theme.border}` }}>
          <h3 style={{ color: theme.text, margin: '0 0 6px 0', fontSize: '18px', fontWeight: '800' }}>📣 {strings.adminBroadcast}</h3>
          <p style={{ color: theme.textMuted, fontSize: '12px', margin: '0 0 18px 0' }}>{strings.adminSubscribers}: {stats?.marketingSubscribers ?? 0}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input type="text" placeholder={strings.adminBroadcastSubject} value={broadcast.subject} onChange={e => setBroadcast(b => ({ ...b, subject: e.target.value }))} style={{ ...styles.authInput, backgroundColor: theme.factorBg, color: theme.text, borderColor: theme.border }} />
            <input type="text" placeholder={strings.adminBroadcastTitle} value={broadcast.title} onChange={e => setBroadcast(b => ({ ...b, title: e.target.value }))} style={{ ...styles.authInput, backgroundColor: theme.factorBg, color: theme.text, borderColor: theme.border }} />
            <textarea placeholder={strings.adminBroadcastMessage} value={broadcast.message} onChange={e => setBroadcast(b => ({ ...b, message: e.target.value }))} rows={5} style={{ ...styles.authInput, backgroundColor: theme.factorBg, color: theme.text, borderColor: theme.border, resize: 'vertical', fontFamily: 'inherit' }} />
            {broadcastResult && <p style={{ color: broadcastResult.includes(strings.adminBroadcastSuccess) ? '#10b981' : '#ef4444', fontSize: '13px', margin: 0, fontWeight: '600' }}>{broadcastResult}</p>}
            <button onClick={sendBroadcast} disabled={isSending} style={{ ...styles.heroButton, backgroundColor: '#f59e0b', width: '100%', cursor: isSending ? 'wait' : 'pointer', opacity: isSending ? 0.75 : 1 }}>
              {isSending ? '...' : strings.adminSendBroadcast}
            </button>
          </div>
        </div>
      </div>
  );
}

function NotFoundPage({ theme, strings }) {
  return (
      <div className="fade-in-up" style={{ ...styles.pageHero, padding: "80px 20px" }}>
        <h1 style={{ fontSize: "72px", margin: 0, color: theme.border }}>404</h1>
        <h2 style={{ ...styles.heroTitle, color: theme.text }}>{strings.pageNotFound}</h2>
        <Link to="/" style={{...styles.heroButton, backgroundColor: theme.accent}}>{strings.returnShop}</Link>
      </div>
  );
}

const styles = {
  appContainer: { minHeight: "100vh", display: "flex", flexDirection: "column" },
  navbar: { position: "sticky", top: 0, zIndex: 900, padding: "0 20px" },
  navContent: { maxWidth: "1200px", height: "70px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" },
  logoLink: { textDecoration: "none" },
  logo: { height: "72px", width: "auto", objectFit: "contain", display: "block" },
  navLinks: { display: "flex", gap: "24px" },
  navLink: { textDecoration: "none", fontSize: "15px" },
  navActions: { display: "flex", alignItems: "center", gap: "12px" },
  themeToggle: { border: "none", padding: "8px 14px", borderRadius: "10px", fontWeight: "600", fontSize: "13px", cursor: "pointer" },
  cartNavButton: { display: "flex", alignItems: "center", border: "none", padding: "8px 16px", borderRadius: "10px", cursor: "pointer", fontWeight: "600", gap: "6px" },
  cartBadge: { color: "#ffffff", fontSize: "11px", padding: "2px 6px", borderRadius: "20px", marginLeft: "4px" },

  mainContent: { maxWidth: "1200px", width: "100%", margin: "0 auto", padding: "40px 20px", flex: 1, boxSizing: "border-box" },

  /* HOME STYLING */
  homeContainer: { display: 'flex', flexDirection: 'column', gap: '40px' },
  premiumHeroCard: { position: 'relative', overflow: 'hidden', padding: '50px 30px', borderRadius: '24px', border: '1px solid', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  meshPatternBg: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'radial-gradient(rgba(0,0,0,0.15) 1px, transparent 0)', backgroundSize: '8px 8px', pointerEvents: 'none', opacity: 0.4 },
  bannerImageFrame: { width: '100%', maxWidth: '850px', margin: '20px 0', overflow: 'hidden', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.4)' },
  lostmarybanner: { width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'cover', display: 'block' },
  heroNoticeText: { fontSize: '18px', fontWeight: 'bold', margin: '15px 0' },
  showroomSection: { display: 'flex', flexDirection: 'column', gap: '20px' },
  showroomTitle: { fontSize: '22px', fontWeight: '800', letterSpacing: '0.5px' },
  showroomGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' },
  showroomCard: { display: 'flex', alignItems: 'center', gap: '15px', padding: '16px', borderRadius: '16px', border: '1px solid', cursor: 'pointer' },
  showroomThumb: { width: '70px', height: '70px', objectFit: 'contain', borderRadius: '8px' },
  showroomCardMeta: { display: 'flex', flexDirection: 'column', gap: '4px' },

  /* NEWS FEED & IMAGE STREAM STYLING */
  newsPageContainer: { display: 'flex', flexDirection: 'column', gap: '30px' },
  sectionDividerHeader: { fontSize: '20px', fontWeight: '700', borderBottom: '2px dashed #404040', paddingBottom: '8px', margin: '10px 0' },
  imageStreamContainer: { display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '14px', scrollbarWidth: 'thin' },
  streamImageFrame: { flex: '0 0 160px', height: '160px', borderRadius: '14px', border: '1px solid', overflow: 'hidden', position: 'relative' },
  streamImg: { width: '100%', height: '100%', objectFit: 'cover' },
  streamOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, padding: '6px', textAlign: 'center', opacity: 0.9 },
  newsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px', marginTop: '10px' },
  newsCard: { display: 'flex', flexDirection: 'column', borderRadius: '20px', border: '1px solid', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
  newsImageWrapper: { width: '100%', height: '180px', overflow: 'hidden', backgroundColor: '#000' },
  newsCardImage: { width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 },
  newsContentBlock: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '8px' },
  newsDateBadge: { fontSize: '12px', fontWeight: '600' },
  newsArticleTitle: { fontSize: '18px', fontWeight: '700', margin: 0 },
  newsArticleDesc: { fontSize: '14px', lineHeight: '1.5', margin: 0 },

  detailsPageLayout: { padding: "32px", borderRadius: "24px", border: "1px solid", boxSizing: "border-box" },
  backButton: { background: "none", border: "none", fontSize: "14px", fontWeight: "600", cursor: "pointer", marginBottom: "24px", padding: 0, display: "flex", alignItems: "center" },
  detailsFlexContainer: { display: "flex", flexWrap: "wrap", gap: "40px" },
  detailsImageWrapper: { flex: "1 1 400px", minWidth: "300px", height: "400px", borderRadius: "20px", position: "relative", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" },
  detailsLargeImage: { width: "100%", height: "100%", objectFit: "contain" },
  detailsInfoPanel: { flex: "1 2 500px", display: "flex", flexDirection: "column", justifyContent: "center" },
  detailMainTitle: { fontSize: "32px", fontWeight: "800", margin: "12px 0 16px 0" },
  detailPriceTag: { display: "flex", alignItems: "baseline", gap: "4px", marginBottom: "20px" },
  detailLongDescription: { fontSize: "15px", lineHeight: "1.6", margin: "0 0 24px 0" },
  specificationsBox: { padding: "20px", borderRadius: "14px", border: "1px solid" },

  footer: { padding: "24px 20px", marginTop: "auto" },
  footerContent: { maxWidth: "1200px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" },
  copyright: { fontSize: "14px", margin: 0 },
  warning: {fontSize: "14px", fontFamily: "Franklin Gothic Medium"},
  footerRight: { display: "flex", alignItems: "center", gap: "12px" },
  footerEmail: { fontSize: "14px", textDecoration: "none", fontWeight: "500" },
  footerLink: { fontSize: "14px", textDecoration: "none", fontWeight: "500" },

  pageHero: { textAlign: "center", padding: "60px 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
  heroBadge: { padding: "6px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: "600", marginBottom: "16px" },
  heroTitle: { fontSize: "36px", fontFamily: "Alef", fontWeight: "800", margin: "0 0 16px 0", maxWidth: "600px", lineHeight: "1.2" },
  heroSubtitle: { fontSize: "20px", fontFamily: "system-ui", maxWidth: "650px", lineHeight: "1.6", margin: "0 0 32px 0" },
  heroButton: { color: "#ffffff", textDecoration: "none", padding: "14px 28px", borderRadius: "12px", fontSize: "15px", fontWeight: "600", border: "none", cursor: "pointer" },

  pageHeader: { marginBottom: "32px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" },
  storeTitle: { fontSize: "26px", fontWeight: "800", fontFamily: "Alef", margin: "0 0 6px 0" },
  storeSubtitle: { fontSize: "14px", margin: 0 },

  filterContainer: { marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' },
  filterBtn: { padding: "8px 16px", borderRadius: "20px", border: "1px solid", fontSize: "13px", fontWeight: "600", cursor: "pointer" },

  searchContainer: { position: 'relative', marginTop: '20px', width: '100%', maxWidth: '380px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  searchInput: { width: '100%', padding: '12px 40px 12px 16px', borderRadius: '12px', border: '1px solid', fontSize: '14px', outline: 'none', transition: 'all 0.3s ease' },
  searchClearBtn: { position: 'absolute', right: '14px', background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', outline: 'none', padding: 0 },

  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "24px", padding: "20px 0" },
  card: { width: "100%", maxWidth: "340px", margin: "0 auto", textAlign: "left", overflow: "hidden", borderRadius: "20px", border: "1px solid", position: "relative" },
  imageContainer: { position: "relative", height: "200px" },
  image: { width: "100%", height: "100%", objectFit: "cover" },
  badge: { position: "absolute", top: "12px", left: "12px", backgroundColor: "#0f172a", color: "#ffffff", padding: "4px 10px", borderRadius: "10px", fontSize: "11px", fontWeight: "600" },

  soldOutOverlay: { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", padding: "10px 20px", borderRadius: "8px", fontWeight: "900", fontSize: "15px", zIndex: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.25)", border: "1px solid" },
  detailsContainer: { padding: "24px" },
  productName: { fontSize: "18px", fontWeight: "700", margin: "0 0 8px 0" },
  productDescription: { fontSize: "13px", lineHeight: "1.5", margin: "0 0 16px 0", height: "40px", overflow: "hidden" },
  priceRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: '16px' },
  priceTag: { display: "flex", alignItems: "baseline" },
  currency: { fontSize: "14px", fontWeight: "600" },
  price: { fontSize: "22px", fontWeight: "800" },

  actionRow: { display: "flex", gap: "10px" },
  actionBtn: { border: "none", padding: "12px", borderRadius: "10px", fontSize: "13px", fontWeight: "700", flex: 1, transition: "all 0.2s ease" },

  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.7)", backdropFilter: "blur(4px)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" },
  drawer: { position: 'fixed', top: 0, bottom: 0, width: "100%", maxWidth: "420px", height: "100%", display: "flex", flexDirection: "column", boxShadow: "-12px 0 40px rgba(0,0,0,0.6)" },
  modal: { width: "100%", maxWidth: "460px", padding: "24px", borderRadius: "20px", border: "1px solid", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5)", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexShrink: 0 },
  modalBody: { display: "flex", flexDirection: "column" },
  instantProductRow: { display: "flex", alignItems: "center", gap: "14px" },
  drawerHeader: { padding: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  drawerTitle: { fontSize: "18px", fontWeight: "700", margin: 0 },
  closeButton: { background: "none", border: "none", fontSize: "18px", color: "#94a3b8", cursor: "pointer" },
  drawerBody: { padding: "16px 24px", flex: 1, overflowY: "auto" },
  emptyCart: { textAlign: "center", padding: "60px 0", fontSize: "14px" },
  cartItemRow: { display: "flex", alignItems: "center", padding: "14px 0", gap: "2px" },
  cartItemThumb: { width: "62px", height: "62px", objectFit: "cover", borderRadius: "12px" },
  cartItemInfo: { flex: 1 },
  cartItemName: { fontSize: "14px", fontWeight: "700", margin: "0 0 3px 0" },
  cartItemMeta: { fontSize: "12px", margin: 0 },
  deleteItemBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "14px" },
  drawerFooter: { padding: "20px 24px" },
  totalSummaryRow: { display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: "700", marginBottom: "12px" },
  totalPrice: { fontSize: "20px" },
  divider: { border: 0, margin: "12px 0" },

  loadingOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", zIndex: 9999 },
  spinner: { width: "48px", height: "48px", borderStyle: "solid", borderWidth: "4px", borderRadius: "50%", animation: "spin 1s linear infinite", marginBottom: "16px" },
  loadingText: { fontSize: "14px", fontWeight: "500", letterSpacing: "0.5px" },

  ageOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.95)", backdropFilter: "blur(12px)", zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "center", padding: '20px' },
  ageModal: { width: "100%", maxWidth: "440px", padding: "40px 32px", borderRadius: "24px", border: "1px solid", textAlign: "center", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.8)" },
  ageIcon: { fontSize: "56px", marginBottom: "16px" },
  ageTitle: { fontSize: "24px", fontWeight: "800", margin: "0 0 12px 0" },
  ageSubtitle: { fontSize: "14px", lineHeight: "1.6", margin: 0 },
  ageActionsColumn: { display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" },
  ageCancelBtn: { background: "none", border: "none", fontSize: "13px", fontWeight: "600", cursor: "pointer", padding: "8px", textDecoration: "underline" },
};

export default App;